import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { flushSync } from 'react-dom';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';
import {
  ArrowRight,
  CreditCard,
  Calendar,
  ChevronDown,
  Clock,
  Info,
  Sparkles,
  ShoppingCart,
  User,
  Heart,
  Paperclip,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  cn,
  formatDateDdMonYyyy,
  addMonthsAnnualBundleEnd,
  addCalendarMonthsYmd,
  localCalendarTodayYmd,
  homeComingThirdOfMonthAfterCurrentCalendarMonth,
} from '../../lib/utils';
import { useToast } from '../../hooks/use-toast';
import { useCart } from '../../context/CartContext';
import { useAuth } from '../../context/AuthContext';
import { useCurrency } from '../../context/CurrencyContext';
import { getAuthHeaders } from '../../lib/authHeaders';
import { pickTierIndexForDashboard } from './dashboardUpcomingHelpers';
import DashboardUpcomingProgramsIrisBloom from './DashboardUpcomingProgramsIrisBloom';
import { getApiUrl } from '../../lib/config';
import { splitPhoneForCart } from '../../lib/dashboardCartPrefill';
import { irisYearLabelNoPeriod } from '../../lib/irisJourney';
import { resolveIndiaDiscountRule, applyIndiaDiscountRuleToBase } from '../../lib/indiaClientPricing';

/** Mirrors backend `_HOME_COMING_INCLUDES` shorts — subtitle for Divine Iris bundle. */
const DIVINE_IRIS_HOME_COMING_PROGRAMS_LABEL =
  'AWRP · MMM · Turbo Release · Meta Downloads';

const HEART_QUOTE = 'You are exactly where you need to be — trust the becoming.';

/** Sacred Home / Home Coming annual bundle always anchors to this day-of-month (product rule). */
const HOME_COMING_BUNDLE_DOM = 3;

function anchorHomeComingBundleStartYmd(ymd) {
  if (!ymd || typeof ymd !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(ymd)) return ymd;
  return `${ymd.slice(0, 8)}${String(HOME_COMING_BUNDLE_DOM).padStart(2, '0')}`;
}

/** Full Home Coming bundle contents (shown in ALL CAPS). Shared with Household overview. */
export const HOME_COMING_BUNDLE_INCLUDES_LINES = [
  '12-MONTH AWRP',
  '6-MONTH MMM',
  '4 TURBO RELEASE',
  '2 META DOWNLOADS',
];

const HOME_COMING_PILLAR_SHORT = {
  awrp: 'AWRP',
  mmm: 'MMM',
  turbo: 'TR',
  meta: 'META',
};

/** Earliest dated slot per pillar (Home Coming sessions from ledger or subscription). */
function earliestAvailedYmdByPillar(sessions) {
  const buckets = { awrp: [], mmm: [], turbo: [], meta: [] };
  for (const s of sessions || []) {
    const p = String(s?.program || '').toLowerCase();
    const d = String(s?.date || '').slice(0, 10);
    if (buckets[p] && /^\d{4}-\d{2}-\d{2}$/.test(d)) buckets[p].push(d);
  }
  const minStr = (arr) => (arr.length ? arr.slice().sort()[0] : null);
  return {
    awrp: minStr(buckets.awrp),
    mmm: minStr(buckets.mmm),
    turbo: minStr(buckets.turbo),
    meta: minStr(buckets.meta),
  };
}

const ALL_PAY_MODES = [
  { value: 'full', label: 'PAY IN FULL' },
  { value: 'emi_monthly', label: 'EMI — MONTHLY' },
  { value: 'emi_quarterly', label: 'EMI — QUARTERLY' },
  { value: 'emi_yearly', label: 'EMI — YEARLY' },
  { value: 'emi_flexi', label: 'FLEXI — ANY AMOUNT, ANY TIME' },
];

/** PAY IN FULL always; EMI/Flexi on unless admin explicitly hid that option (Dashboard access → false). */
function isAnnualOfferPayModeEnabled(homeData, mode) {
  if (mode === 'full') return true;
  if (!homeData || typeof homeData !== 'object') return false;
  const keyByMode = {
    emi_monthly: 'annual_package_offer_monthly_emi_visible',
    emi_quarterly: 'annual_package_offer_quarterly_emi_visible',
    emi_yearly: 'annual_package_offer_yearly_emi_visible',
    emi_flexi: 'annual_package_offer_flexi_visible',
  };
  const key = keyByMode[mode];
  if (!key) return false;
  return homeData[key] !== false;
}

function emiInstallmentCount(mode, durationMonths) {
  const d = Math.max(1, Number(durationMonths) || 12);
  if (mode === 'emi_monthly') return d;
  if (mode === 'emi_quarterly') return Math.max(1, Math.ceil(d / 3));
  if (mode === 'emi_yearly') return Math.max(1, Math.ceil(d / 12));
  return 1;
}

/** Pick admin catalog offer for the member's pricing hub (offer_total keys: INR, AED, USD, …). */
function offerTotalForCurrency(offerTotal, currency) {
  if (!offerTotal || typeof offerTotal !== 'object') return null;
  const c = (currency || 'inr').toString();
  const up = c.toUpperCase();
  const lo = c.toLowerCase();
  for (const k of [up, lo, c]) {
    if (k in offerTotal && offerTotal[k] != null && String(offerTotal[k]).trim() !== '') {
      const n = Number(offerTotal[k]);
      if (!Number.isNaN(n)) return n;
    }
  }
  for (const [key, val] of Object.entries(offerTotal)) {
    if (String(key).toLowerCase() === lo) {
      const n = Number(val);
      if (!Number.isNaN(n)) return n;
    }
  }
  return null;
}

/** Split ``total`` into ``n`` equal parts (2 dp); remainder cents spread across first rows. */
function splitAmountsEqually(total, n) {
  const t = Math.max(0, Number(total) || 0);
  const count = Math.max(1, Math.floor(Number(n)) || 1);
  if (t <= 0) return Array(count).fill(0);
  const cents = Math.round(t * 100);
  const base = Math.floor(cents / count);
  const rem = cents - base * count;
  const arr = [];
  for (let i = 0; i < count; i++) {
    arr.push((base + (i < rem ? 1 : 0)) / 100);
  }
  return arr;
}

/** Home Coming illustrative schedule: every installment due on this calendar day (clamped to month length). */
const HOME_COMING_EMI_DUE_DOM = 27;
/** First EMI is in the month *before* bundle start (e.g. 27 Apr when the batch starts 3 May). */
const HOME_COMING_EMI_FIRST_MONTH_OFFSET = -1;

function pad2Ymd(n) {
  return String(n).padStart(2, '0');
}

function utcYmdWithDom(year, month0, dom) {
  const dim = new Date(Date.UTC(year, month0 + 1, 0)).getUTCDate();
  const day = Math.min(dom, dim);
  return `${year}-${pad2Ymd(month0 + 1)}-${pad2Ymd(day)}`;
}

function shiftCalendarMonths(year, month0, delta) {
  const t = month0 + delta;
  const yy = year + Math.floor(t / 12);
  const mm = ((t % 12) + 12) % 12;
  return { y: yy, m0: mm };
}

function homeComingInstallmentDueYmd(startYmd, installmentIndex0, mode) {
  if (!startYmd || !/^\d{4}-\d{2}-\d{2}$/.test(startYmd)) return '';
  const [ys, ms] = startYmd.split('-').map(Number);
  let offsetMonths = HOME_COMING_EMI_FIRST_MONTH_OFFSET;
  if (mode === 'emi_monthly') offsetMonths = HOME_COMING_EMI_FIRST_MONTH_OFFSET + installmentIndex0;
  else if (mode === 'emi_quarterly') offsetMonths = HOME_COMING_EMI_FIRST_MONTH_OFFSET + installmentIndex0 * 3;
  else offsetMonths = HOME_COMING_EMI_FIRST_MONTH_OFFSET + installmentIndex0 * 12;
  const { y, m0 } = shiftCalendarMonths(ys, ms - 1, offsetMonths);
  return utcYmdWithDom(y, m0, HOME_COMING_EMI_DUE_DOM);
}

/** CRM rows often land on the batch month (e.g. 27 May); display one month earlier (27 Apr). */
function homeComingDueOneMonthEarlier(ymd) {
  if (!ymd || !/^\d{4}-\d{2}-\d{2}$/.test(ymd)) return ymd;
  const [ys, ms] = ymd.split('-').map(Number);
  const { y, m0 } = shiftCalendarMonths(ys, ms - 1, -1);
  return utcYmdWithDom(y, m0, HOME_COMING_EMI_DUE_DOM);
}

/** If a row still shows the raw CRM due date, shift one month earlier for advance billing. */
function applyHomeComingCrmDueAdvanceShift(rows, emis) {
  const list = Array.isArray(emis) ? emis : [];
  if (!list.length) return rows;
  const crmDueByN = new Map(
    list.map((e) => [Number(e.number), String(e.due_date || '').trim().slice(0, 10)]),
  );
  return rows.map((r) => {
    if (!String(r.key || '').startsWith('emi-') || !r.due || !/^\d{4}-\d{2}-\d{2}$/.test(r.due)) return r;
    const crmDue = crmDueByN.get(r.n);
    if (crmDue && crmDue === r.due) {
      return { ...r, due: homeComingDueOneMonthEarlier(r.due) };
    }
    return r;
  });
}

function buildEmiPreview(mode, total, startYmd, durationMonths) {
  if (!total || total <= 0 || mode === 'full' || mode === 'emi_flexi') return [];
  const n = emiInstallmentCount(mode, durationMonths);
  const amounts = splitAmountsEqually(total, n);
  const basisYmd =
    startYmd && /^\d{4}-\d{2}-\d{2}$/.test(startYmd)
      ? anchorHomeComingBundleStartYmd(startYmd)
      : new Date().toISOString().slice(0, 10);
  const out = [];
  for (let i = 0; i < n; i += 1) {
    out.push({
      n: i + 1,
      due: homeComingInstallmentDueYmd(basisYmd, i, mode),
      amount: amounts[i] ?? 0,
    });
  }
  return out;
}

/** Live EMI rows from Sacred Exchange (`GET /student/home` financials.emis), sorted by installment #. */
function buildPaymentScheduleRowsFromCrmEmis(emis, scheduleBasisYmd, paymentMode = 'emi_monthly') {
  const list = Array.isArray(emis) ? emis : [];
  const sorted = [...list].sort((a, b) => Number(a.number) - Number(b.number));
  const basis =
    scheduleBasisYmd && /^\d{4}-\d{2}-\d{2}$/.test(scheduleBasisYmd)
      ? anchorHomeComingBundleStartYmd(scheduleBasisYmd)
      : '';
  return sorted.map((e) => {
    const n = Number(e.number);
    const idx = Number.isFinite(n) && n > 0 ? n - 1 : 0;
    const crmDue = (e.due_date || '').toString().slice(0, 10);
    let due = basis ? homeComingInstallmentDueYmd(basis, idx, paymentMode) : '';
    if (!due || (crmDue && due === crmDue)) {
      due =
        crmDue && /^\d{4}-\d{2}-\d{2}$/.test(crmDue)
          ? homeComingDueOneMonthEarlier(crmDue)
          : crmDue;
    }
    return {
      key: `emi-${e.number}`,
      n,
      due,
      dueDisplay: null,
      amount: Number(e.amount) || 0,
      amountDisplay: null,
    };
  });
}

function scaleScheduleRowsToTarget(rows, targetTotal) {
  const list = Array.isArray(rows) ? rows : [];
  const target = Number(targetTotal);
  if (!Number.isFinite(target) || target <= 0 || list.length === 0) return list;
  const base = list.reduce((sum, r) => sum + (Number.isFinite(Number(r.amount)) ? Number(r.amount) : 0), 0);
  if (!Number.isFinite(base) || base <= 0) return list;
  const scale = target / base;
  const out = list.map((r) => ({ ...r, amount: Math.round((Number(r.amount) || 0) * scale * 100) / 100 }));
  const drift = Math.round((target - out.reduce((s, r) => s + (Number(r.amount) || 0), 0)) * 100) / 100;
  if (Math.abs(drift) >= 0.01) {
    const lastIdx = out.length - 1;
    out[lastIdx] = { ...out[lastIdx], amount: Math.round(((Number(out[lastIdx].amount) || 0) + drift) * 100) / 100 };
  }
  return out;
}

function coerceDiscountPercent(value) {
  if (value == null || value === '') return null;
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  const raw = String(value).replace(/,/g, '').trim();
  if (!raw) return null;
  const m = raw.match(/[-+]?\d*\.?\d+/);
  if (!m) return null;
  const n = Number(m[0]);
  return Number.isFinite(n) ? n : null;
}

/** Rows for the illustrative schedule table (all payment modes). */
function buildPaymentScheduleRows(mode, total, startYmd, durationMonths) {
  const totalNum = Math.max(0, Number(total) || 0);

  if (mode === 'full') {
    let due;
    if (startYmd && /^\d{4}-\d{2}-\d{2}$/.test(startYmd)) {
      due = startYmd;
    } else {
      due = new Date().toISOString().slice(0, 10);
    }
    return [
      {
        key: 'pay-full',
        n: 1,
        due,
        dueDisplay: null,
        amount: totalNum,
        amountDisplay: null,
      },
    ];
  }

  if (mode === 'emi_flexi') {
    const rows = [
      {
        key: 'flex-a',
        n: 1,
        due: null,
        dueDisplay: 'Whenever you choose',
        amount: null,
        amountDisplay: 'Any amount you wish',
      },
    ];
    if (totalNum > 0) {
      rows.push({
        key: 'flex-ref',
        n: '—',
        due: null,
        dueDisplay: 'Balance reference (quoted total)',
        amount: totalNum,
        amountDisplay: null,
      });
    }
    return rows;
  }

  if (totalNum <= 0) {
    return [
      {
        key: 'emi-pending',
        n: 1,
        due: null,
        dueDisplay: 'Confirmed with your host',
        amount: null,
        amountDisplay: 'Installment amounts when total is set',
      },
    ];
  }

  const emi = buildEmiPreview(mode, totalNum, startYmd, durationMonths);
  return emi.map((r) => ({
    key: `emi-${r.n}`,
    n: r.n,
    due: r.due,
    dueDisplay: null,
    amount: r.amount,
    amountDisplay: null,
  }));
}

function formatSchedulePayTag(raw) {
  const k = String(raw || '').trim().toLowerCase();
  if (!k) return 'As set with host';
  if (k === 'gpay_upi' || k === 'gpay' || k === 'upi') return 'GPay / UPI';
  if (k === 'bank_transfer' || k === 'bank' || k === 'neft' || k === 'rtgs' || k === 'imps') return 'Bank transfer';
  if (k === 'cash_deposit' || k === 'cash') return 'Cash deposit';
  if (k === 'stripe' || k === 'card' || k === 'cards') return 'Stripe';
  if (k === 'any' || k === 'multiple' || k === 'all') return 'GPay / UPI / Bank';
  if (k === 'razorpay') return 'Razorpay';
  return k.replace(/_/g, ' ');
}

/**
 * Dedicated Home Coming / annual bundle page: purchase options, illustrative EMI plan, and link to live payment status on Financials.
 */
export default function AnnualPackagePurchasePage() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user } = useAuth();
  const { syncProgramLineItem, itemCount: cartCount } = useCart();
  const { baseCurrency, symbol, toDisplay } = useCurrency();
  const [loading, setLoading] = useState(true);
  const [homeData, setHomeData] = useState(null);
  const [portalOrders, setPortalOrders] = useState([]);
  const [quote, setQuote] = useState(null);
  const [quoteLoading, setQuoteLoading] = useState(false);
  const [prefsLoaded, setPrefsLoaded] = useState(false);
  const prefsSaveTimer = useRef(null);
  const prefsInitDone = useRef(false);

  const [desiredStart, setDesiredStart] = useState('');
  /** Flexi first row: amount member chooses before Pay · Stripe. */
  const [flexiAmountInput, setFlexiAmountInput] = useState('');
  const [priorSacredCycleOpen, setPriorSacredCycleOpen] = useState(false);
  const [paymentMode, setPaymentMode] = useState('full');
  /** Home Coming annual path: online vs offline only (no in-person seat on this bundle). */
  const [annualParticipationMode, setAnnualParticipationMode] = useState('online');
  const [emiNotes, setEmiNotes] = useState('');

  const refresh = useCallback(() => {
    setLoading(true);
    const headers = getAuthHeaders();
    Promise.all([
      axios.get(`${getApiUrl()}/student/home`, { withCredentials: true, headers }),
      axios
        .get(`${getApiUrl()}/student/orders`, { withCredentials: true, headers })
        .catch(() => ({ data: { orders: [] } })),
    ])
      .then(([homeRes, ordRes]) => {
        setHomeData(homeRes.data);
        setPortalOrders(Array.isArray(ordRes.data?.orders) ? ordRes.data.orders : []);
      })
      .catch(() => {
        setHomeData(null);
        setPortalOrders([]);
        toast({
          title: 'Could not load dashboard',
          variant: 'destructive',
        });
      })
      .finally(() => setLoading(false));
  }, [toast]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const pinnedProgram = useMemo(() => {
    if (homeData?.home_coming_catalog_program) return homeData.home_coming_catalog_program;
    const list = homeData?.upcoming_programs || [];
    const pin = list.find((p) => p.dashboard_annual_product_pin);
    return pin || list[0] || null;
  }, [homeData?.home_coming_catalog_program, homeData?.upcoming_programs]);

  const pkg = homeData?.package || {};
  const fin = homeData?.financials || {};
  const sacredMoneyCurrency = useMemo(
    () => (baseCurrency || fin.currency || 'aed').toString().toUpperCase(),
    [baseCurrency, fin.currency],
  );
  const emis = fin.emis || [];
  const emiByNumber = useMemo(() => {
    const m = new Map();
    for (const e of emis) {
      if (!e || e.number == null) continue;
      const n = Number(e.number);
      if (!Number.isNaN(n)) m.set(n, e);
    }
    return m;
  }, [emis]);

  /** Latest paid portal checkout for Home Coming — CRM EMIs can lag after Stripe. */
  const portalHomeComingPaidInfo = useMemo(() => {
    const list = Array.isArray(portalOrders) ? portalOrders : [];
    const hub = String(baseCurrency || '').toLowerCase();
    const candidates = list.filter((o) => {
      if (!o || o.is_free) return false;
      if (String(o.payment_status || '').toLowerCase() !== 'paid') return false;
      const title = String(o.item_title || '').toLowerCase();
      if (!title.includes('home coming')) return false;
      const amt = Number(o.amount);
      if (!Number.isFinite(amt) || amt <= 0) return false;
      const cur = String(o.currency || '').toLowerCase();
      if (hub && cur && cur !== hub) return false;
      return true;
    });
    candidates.sort((a, b) => {
      const ta = new Date(a.created_at || a.updated_at || 0).getTime();
      const tb = new Date(b.created_at || b.updated_at || 0).getTime();
      return tb - ta;
    });
    const o = candidates[0];
    if (!o) return null;
    const when = String(o.created_at || o.updated_at || '');
    return {
      amount: Number(o.amount),
      currency: String(o.currency || hub || 'aed').toLowerCase(),
      stripeSessionId: String(o.stripe_session_id || '').trim(),
      paidAtYmd: when.slice(0, 10),
      itemTitle: o.item_title || '',
    };
  }, [portalOrders, baseCurrency]);
  const schedulePayTag = useMemo(() => {
    const pref = (homeData?.preferred_payment_method || '').trim();
    const indiaTag = (homeData?.client_india_pricing?.india_payment_method || '').trim();
    const finMode = (fin.payment_mode || '').trim();
    return formatSchedulePayTag(pref || indiaTag || finMode);
  }, [homeData?.preferred_payment_method, homeData?.client_india_pricing?.india_payment_method, fin.payment_mode]);
  const durationMonths = Math.max(1, Number(pkg.duration_months) || 12);
  const catalogFrom = (pkg.catalog_valid_from || '').trim().slice(0, 10);
  const catalogTo = (pkg.catalog_valid_to || '').trim().slice(0, 10);
  const hc = homeData?.home_coming;
  const subtitleFourPrograms =
    hc?.includes?.length > 0
      ? hc.includes.map((i) => (i && typeof i === 'object' ? i.short : '') || '').filter(Boolean).join(' · ') ||
        DIVINE_IRIS_HOME_COMING_PROGRAMS_LABEL
      : DIVINE_IRIS_HOME_COMING_PROGRAMS_LABEL;

  const catalogBundle = homeData?.annual_catalog_bundle;
  const catalogOfferTotal = catalogBundle?.offer_total;
  const catalogAmountHub = useMemo(
    () => offerTotalForCurrency(catalogOfferTotal, baseCurrency),
    [catalogOfferTotal, baseCurrency],
  );
  const catalogDisplayAmount = catalogAmountHub != null ? toDisplay(catalogAmountHub) : null;

  /** CRM-only discount on catalog list (same basis as Sacred Home quotes); excludes site portal default %. */
  const catalogCourtesyBreakdown = useMemo(() => {
    if (catalogAmountHub == null || !(Number(catalogAmountHub) > 0) || !homeData) return null;
    const src = homeData.client_discount_source;
    if (!src) return null;
    const hasBands =
      Array.isArray(src.india_discount_member_bands) && src.india_discount_member_bands.length > 0;
    const hasPct = src.india_discount_percent != null && src.india_discount_percent !== '';
    if (!hasBands && !hasPct) return null;
    const fam = homeData.immediate_family;
    const n = Math.max(1, 1 + (Array.isArray(fam) ? fam.length : 0));
    const cp = {
      india_discount_percent: coerceDiscountPercent(src.india_discount_percent),
      india_discount_member_bands: src.india_discount_member_bands,
    };
    const rule = resolveIndiaDiscountRule(cp, n, 0);
    const hubCur = (baseCurrency || 'inr').toLowerCase();
    if (rule.mode === 'amount' && rule.amountInr > 0 && hubCur !== 'inr') {
      return null;
    }
    const listNum = Number(catalogAmountHub);
    const applied = applyIndiaDiscountRuleToBase(listNum, rule);
    const discountAmt = Math.round(Number(applied.discountAmt || 0) * 100) / 100;
    if (discountAmt <= 0) return null;
    const finalNum = Math.max(0, Math.round((listNum - discountAmt) * 100) / 100);
    const listDisplay = toDisplay(listNum);
    const finalDisplay = toDisplay(finalNum);
    const pctLabel =
      applied.discountKind === 'percent' && applied.discountNominalPercent != null
        ? `${Number(applied.discountNominalPercent).toFixed(1).replace(/\.0$/, '')}%`
        : null;
    return {
      ruleLabel: rule.fromBand ? 'Group offer' : 'Special offer',
      discountAmt,
      finalNum,
      listDisplay,
      finalDisplay,
      pctLabel,
      isAmountBand: rule.mode === 'amount' && rule.amountInr > 0,
    };
  }, [catalogAmountHub, homeData, baseCurrency, toDisplay]);

  const homeComingCourtesyRule = useMemo(() => {
    if (!homeData) return null;
    const src = homeData.client_discount_source;
    if (!src) return null;
    const hasBands =
      Array.isArray(src.india_discount_member_bands) && src.india_discount_member_bands.length > 0;
    const hasPct = src.india_discount_percent != null && src.india_discount_percent !== '';
    if (!hasBands && !hasPct) return null;
    const fam = homeData.immediate_family;
    const n = Math.max(1, 1 + (Array.isArray(fam) ? fam.length : 0));
    const cp = {
      india_discount_percent: coerceDiscountPercent(src.india_discount_percent),
      india_discount_member_bands: src.india_discount_member_bands,
    };
    return resolveIndiaDiscountRule(cp, n, 0);
  }, [homeData]);

  /** Iris Annual Abundance courtesy on on-file package fee — shown only on this page (Sacred Exchange uses raw totals). */
  const onFilePackageCourtesyBreakdown = useMemo(() => {
    const baseRaw = Number(fin.base_package_fee || fin.total_fee || 0);
    if (!(baseRaw > 0) || !homeData) return null;
    const src = homeData.client_discount_source;
    if (!src) return null;
    const hasBands =
      Array.isArray(src.india_discount_member_bands) && src.india_discount_member_bands.length > 0;
    const hasPct = src.india_discount_percent != null && src.india_discount_percent !== '';
    if (!hasBands && !hasPct) return null;
    const fam = homeData.immediate_family;
    const n = Math.max(1, 1 + (Array.isArray(fam) ? fam.length : 0));
    const cp = {
      india_discount_percent: coerceDiscountPercent(src.india_discount_percent),
      india_discount_member_bands: src.india_discount_member_bands,
    };
    const rule = resolveIndiaDiscountRule(cp, n, 0);
    const feeCur = String(fin.currency || baseCurrency || 'inr').toLowerCase();
    if (rule.mode === 'amount' && rule.amountInr > 0 && feeCur !== 'inr') {
      return null;
    }
    const applied = applyIndiaDiscountRuleToBase(baseRaw, rule);
    const discountAmt = Math.round(Number(applied.discountAmt || 0) * 100) / 100;
    if (discountAmt <= 0) return null;
    const finalNum = Math.max(0, Math.round((baseRaw - discountAmt) * 100) / 100);
    const pctLabel =
      applied.discountKind === 'percent' && applied.discountNominalPercent != null
        ? `${Number(applied.discountNominalPercent).toFixed(1).replace(/\.0$/, '')}%`
        : null;
    return { baseRaw, discountAmt, finalNum, pctLabel };
  }, [fin.base_package_fee, fin.total_fee, fin.currency, homeData, baseCurrency]);

  const onFileCourtesyAdjustedTotal = useMemo(() => {
    if (!onFilePackageCourtesyBreakdown) return null;
    const n = Number(onFilePackageCourtesyBreakdown.finalNum);
    return Number.isFinite(n) && n > 0 ? n : null;
  }, [onFilePackageCourtesyBreakdown]);

  const catalogOtherCurrencies = useMemo(() => {
    if (!catalogOfferTotal || typeof catalogOfferTotal !== 'object') return [];
    const hub = (baseCurrency || 'inr').toLowerCase();
    return Object.entries(catalogOfferTotal).filter(
      ([k]) => k && String(k).toLowerCase() !== hub && Number(catalogOfferTotal[k]) > 0,
    );
  }, [catalogOfferTotal, baseCurrency]);

  /** Non-empty catalog amounts (fallback when user's hub key is missing). */
  const catalogAllPositiveEntries = useMemo(() => {
    if (!catalogOfferTotal || typeof catalogOfferTotal !== 'object') return [];
    return Object.entries(catalogOfferTotal).filter(
      ([, v]) =>
        v != null &&
        String(v).trim() !== '' &&
        !Number.isNaN(Number(v)) &&
        Number(v) >= 0,
    );
  }, [catalogOfferTotal]);

  const catalogAllPositiveEntriesCourtesyAdjusted = useMemo(() => {
    if (!homeComingCourtesyRule) return catalogAllPositiveEntries;
    return catalogAllPositiveEntries.map(([k, v]) => {
      const n = Number(v);
      if (!Number.isFinite(n) || n <= 0) return [k, v];
      if (
        homeComingCourtesyRule.mode === 'amount' &&
        Number(homeComingCourtesyRule.amountInr || 0) > 0 &&
        String(k || '').toLowerCase() !== 'inr'
      ) {
        return [k, v];
      }
      const applied = applyIndiaDiscountRuleToBase(n, homeComingCourtesyRule);
      const discountAmt = Number(applied.discountAmt || 0);
      if (!(discountAmt > 0)) return [k, v];
      return [k, Math.max(0, Math.round((n - discountAmt) * 100) / 100)];
    });
  }, [catalogAllPositiveEntries, homeComingCourtesyRule]);

  const primaryCatalogFallbackEntry = useMemo(() => {
    if (!catalogAllPositiveEntries.length) return null;
    const inr = catalogAllPositiveEntries.find(([k]) => String(k || '').toLowerCase() === 'inr');
    return inr || catalogAllPositiveEntries[0] || null;
  }, [catalogAllPositiveEntries]);

  const primaryCatalogFallbackCourtesyBreakdown = useMemo(() => {
    if (!primaryCatalogFallbackEntry) return null;
    const [curRaw, amtRaw] = primaryCatalogFallbackEntry;
    const cur = String(curRaw || '').toLowerCase();
    const listNum = Number(amtRaw);
    if (!Number.isFinite(listNum) || listNum <= 0) return null;
    if (!homeComingCourtesyRule) {
      return {
        currencyKey: cur.toUpperCase(),
        listNum,
        finalNum: listNum,
        discountAmt: 0,
        changed: false,
        pctLabel: null,
      };
    }
    if (
      homeComingCourtesyRule.mode === 'amount' &&
      Number(homeComingCourtesyRule.amountInr || 0) > 0 &&
      cur !== 'inr'
    ) {
      return {
        currencyKey: cur.toUpperCase(),
        listNum,
        finalNum: listNum,
        discountAmt: 0,
        changed: false,
        pctLabel: null,
      };
    }
    const applied = applyIndiaDiscountRuleToBase(listNum, homeComingCourtesyRule);
    const discountAmt = Math.round(Number(applied.discountAmt || 0) * 100) / 100;
    const finalNum = Math.max(0, Math.round((listNum - discountAmt) * 100) / 100);
    const pctLabel =
      applied.discountKind === 'percent' && applied.discountNominalPercent != null
        ? `${Number(applied.discountNominalPercent).toFixed(1).replace(/\.0$/, '')}%`
        : null;
    return {
      currencyKey: cur.toUpperCase(),
      listNum,
      finalNum,
      discountAmt,
      changed: discountAmt > 0.009,
      pctLabel,
    };
  }, [primaryCatalogFallbackEntry, homeComingCourtesyRule]);

  const lap = homeData?.last_annual_package;
  const portalLife = homeData?.annual_portal_lifecycle;
  const lapEndYmd = (lap?.end_date || '').trim().slice(0, 10);
  const lapStartYmd = (lap?.start_date || '').trim().slice(0, 10);
  const todayYmd = new Date().toISOString().slice(0, 10);
  const lapEnded = lapEndYmd.length === 10 && lapEndYmd < todayYmd;
  const isRenewalSeason =
    portalLife?.status === 'expired' ||
    portalLife?.status === 'renewal_due' ||
    lapEnded;
  const lapOnRecordIsCurrent =
    lapStartYmd.length === 10 &&
    lapEndYmd.length === 10 &&
    lapStartYmd <= todayYmd &&
    todayYmd <= lapEndYmd;
  const onRecordCycleBannerTitle = lapOnRecordIsCurrent
    ? 'CURRENT ANNUAL CYCLE · ON RECORD'
    : lapEnded
      ? 'PREVIOUS ANNUAL CYCLE · ON RECORD'
      : 'ANNUAL CYCLE · ON RECORD';
  const showNextSacredHomeEnrollment = isRenewalSeason || !lapOnRecordIsCurrent;
  const preferOnFilePackagePricing =
    lapOnRecordIsCurrent &&
    !isRenewalSeason &&
    Number(fin.total_fee || 0) > 0;
  const needsAdminEmiBackfillHint =
    !portalHomeComingPaidInfo &&
    lapOnRecordIsCurrent &&
    !isRenewalSeason &&
    Number(fin.total_fee || 0) > 0 &&
    (emis.length === 0 ||
      !emis.every((e) => e && String(e.status).toLowerCase() === 'paid')) &&
    Number(fin.remaining ?? 1) > 0;
  const subscriptionStartYmd = (pkg.start_date || '').trim().slice(0, 10);
  const subscriptionEndYmd = (pkg.end_date || '').trim().slice(0, 10);
  const recordStartYmd = /^\d{4}-\d{2}-\d{2}$/.test(subscriptionStartYmd)
    ? subscriptionStartYmd
    : lapStartYmd;
  const recordEndYmd = /^\d{4}-\d{2}-\d{2}$/.test(subscriptionEndYmd)
    ? subscriptionEndYmd
    : lapEndYmd;
  const anyEmiPaid = useMemo(
    () => (emis || []).some((e) => e && String(e.status).toLowerCase() === 'paid'),
    [emis],
  );
  const hasRecordedAnnualPayment =
    anyEmiPaid ||
    (Number(fin.total_fee || 0) > 0 && Number(fin.total_paid || 0) > 0) ||
    portalHomeComingPaidInfo != null;
  /**
   * After payment, cycle dates are fixed. During renewal, CRM package dates may lag behind a completed
   * Stripe order — then we lock from portal + saved desired start until the garden record catches up.
   */
  const membershipCycleDatesLocked =
    hasRecordedAnnualPayment &&
    (/^\d{4}-\d{2}-\d{2}$/.test(recordStartYmd) ||
      (portalHomeComingPaidInfo != null && /^\d{4}-\d{2}-\d{2}$/.test(desiredStart)));
  const desiredStartReachedOrPast =
    /^\d{4}-\d{2}-\d{2}$/.test(desiredStart) && desiredStart <= todayYmd;
  /** Only the *active* on-record cycle counts — not a completed previous window during renewal. */
  const cycleAlreadyStarted =
    lapOnRecordIsCurrent &&
    ((/^\d{4}-\d{2}-\d{2}$/.test(recordStartYmd) && recordStartYmd <= todayYmd) ||
      (/^\d{4}-\d{2}-\d{2}$/.test(lapStartYmd) && lapStartYmd <= todayYmd));
  const renewingChooseNextStart = showNextSacredHomeEnrollment && !membershipCycleDatesLocked;
  const desiredStartInputLocked =
    membershipCycleDatesLocked ||
    (desiredStartReachedOrPast && !renewingChooseNextStart) ||
    cycleAlreadyStarted;
  const cycleDisplayStart = useMemo(() => {
    if (!membershipCycleDatesLocked) {
      return /^\d{4}-\d{2}-\d{2}$/.test(desiredStart)
        ? anchorHomeComingBundleStartYmd(desiredStart)
        : desiredStart;
    }
    const paidYmd = portalHomeComingPaidInfo?.paidAtYmd;
    const re = recordEndYmd;
    const rs = recordStartYmd;
    const stepM = Math.max(1, Math.min(120, parseInt(String(durationMonths), 10) || 12));
    /**
     * CRM ``pkg.start_date`` / ``last_annual_package`` can still show the *previous* Sacred Home window
     * after a renewal checkout. If the latest paid Home Coming order is dated *after* that window ends,
     * advance the displayed cycle start by whole bundle lengths until the payment falls inside the window.
     */
    if (
      portalHomeComingPaidInfo &&
      /^\d{4}-\d{2}-\d{2}$/.test(paidYmd || '') &&
      /^\d{4}-\d{2}-\d{2}$/.test(re) &&
      /^\d{4}-\d{2}-\d{2}$/.test(rs) &&
      paidYmd > re
    ) {
      let s = /^\d{4}-\d{2}-\d{2}$/.test(rs) ? anchorHomeComingBundleStartYmd(rs) : rs;
      let guard = 0;
      while (guard < 24) {
        const winEnd = addMonthsAnnualBundleEnd(s, stepM);
        if (!winEnd || !/^\d{4}-\d{2}-\d{2}$/.test(winEnd)) break;
        if (paidYmd <= winEnd) break;
        const nextS = addCalendarMonthsYmd(s, stepM);
        if (!nextS || nextS === s) break;
        s = anchorHomeComingBundleStartYmd(nextS) || nextS;
        guard += 1;
      }
      if (/^\d{4}-\d{2}-\d{2}$/.test(desiredStart) && desiredStart > re) {
        return anchorHomeComingBundleStartYmd(desiredStart);
      }
      return anchorHomeComingBundleStartYmd(s) || s;
    }
    if (
      portalHomeComingPaidInfo &&
      /^\d{4}-\d{2}-\d{2}$/.test(desiredStart) &&
      /^\d{4}-\d{2}-\d{2}$/.test(re) &&
      desiredStart > re
    ) {
      return anchorHomeComingBundleStartYmd(desiredStart);
    }
    if (/^\d{4}-\d{2}-\d{2}$/.test(recordStartYmd)) return anchorHomeComingBundleStartYmd(recordStartYmd);
    return /^\d{4}-\d{2}-\d{2}$/.test(desiredStart)
      ? anchorHomeComingBundleStartYmd(desiredStart)
      : desiredStart;
  }, [
    membershipCycleDatesLocked,
    portalHomeComingPaidInfo,
    recordStartYmd,
    recordEndYmd,
    desiredStart,
    durationMonths,
  ]);
  const cycleDisplayEnd = useMemo(() => {
    if (
      membershipCycleDatesLocked &&
      /^\d{4}-\d{2}-\d{2}$/.test(recordEndYmd) &&
      !portalHomeComingPaidInfo
    ) {
      return recordEndYmd;
    }
    if (
      membershipCycleDatesLocked &&
      /^\d{4}-\d{2}-\d{2}$/.test(recordEndYmd) &&
      portalHomeComingPaidInfo &&
      /^\d{4}-\d{2}-\d{2}$/.test(recordStartYmd) &&
      anchorHomeComingBundleStartYmd(recordStartYmd) === cycleDisplayStart
    ) {
      return recordEndYmd;
    }
    if (cycleDisplayStart && /^\d{4}-\d{2}-\d{2}$/.test(cycleDisplayStart)) {
      return addMonthsAnnualBundleEnd(cycleDisplayStart, durationMonths) || '';
    }
    return '';
  }, [
    membershipCycleDatesLocked,
    recordEndYmd,
    recordStartYmd,
    cycleDisplayStart,
    durationMonths,
    portalHomeComingPaidInfo,
  ]);

  useEffect(() => {
    if (!prefsLoaded || desiredStartInputLocked) return;
    if (!/^\d{4}-\d{2}-\d{2}$/.test(desiredStart)) return;
    const anchored = anchorHomeComingBundleStartYmd(desiredStart);
    if (anchored !== desiredStart) setDesiredStart(anchored);
  }, [prefsLoaded, desiredStartInputLocked, desiredStart]);

  /** Home Coming bundle has no balance left to collect (portal receipt counts when CRM lags). */
  const homeComingAnnualFullyPaid = useMemo(() => {
    if (portalHomeComingPaidInfo) return true;
    const fee = Number(fin.total_fee || 0);
    const paid = Number(fin.total_paid || 0);
    const remRaw = fin.remaining;
    const rem = remRaw == null || remRaw === '' ? null : Number(remRaw);
    const remOk = rem === null || (!Number.isNaN(rem) && rem <= 0.01);
    if (fee > 0 && paid + 0.02 >= fee && remOk) return true;
    const list = emis || [];
    if (list.length > 0 && list.every((e) => e && String(e.status).toLowerCase() === 'paid')) return true;
    return false;
  }, [portalHomeComingPaidInfo, fin.total_fee, fin.total_paid, fin.remaining, emis]);

  /**
   * Paid-and-locked annual Home Coming: cart and “browse programs” are deferred until the next renewal season.
   * EMI members keep CTAs until every installment is paid.
   */
  const suppressHomeComingCartAndBrowse = useMemo(
    () => membershipCycleDatesLocked && homeComingAnnualFullyPaid,
    [membershipCycleDatesLocked, homeComingAnnualFullyPaid],
  );

  const autoRenewalYear = useMemo(() => {
    const apiYear = parseInt(homeData?.renewal_entering_iris_year, 10) || 1;
    const subYear = parseInt(homeData?.subscription_journey?.iris_year, 10) || 1;
    const ledgerRows = Array.isArray(homeData?.annual_period_ledger) ? homeData.annual_period_ledger : [];
    let maxArchivedYear = 0;
    for (const row of ledgerRows) {
      const y = parseInt(row?.iris_year_at_archive, 10);
      if (!Number.isNaN(y) && y > maxArchivedYear) maxArchivedYear = y;
    }
    const ledgerDerivedYear = maxArchivedYear > 0 ? maxArchivedYear + 1 : 1;
    return Math.min(12, Math.max(1, apiYear, subYear, ledgerDerivedYear));
  }, [homeData?.renewal_entering_iris_year, homeData?.subscription_journey?.iris_year, homeData?.annual_period_ledger]);
  const memberFirstName = useMemo(() => {
    const raw =
      typeof homeData?.user_details?.full_name === 'string'
        ? homeData.user_details.full_name.trim()
        : '';
    if (!raw) return '';
    return raw.split(/\s+/)[0];
  }, [homeData?.user_details?.full_name]);

  const visiblePayModes = useMemo(
    () => ALL_PAY_MODES.filter((m) => isAnnualOfferPayModeEnabled(homeData, m.value)),
    [
      homeData,
      homeData?.annual_package_offer_monthly_emi_visible,
      homeData?.annual_package_offer_quarterly_emi_visible,
      homeData?.annual_package_offer_yearly_emi_visible,
      homeData?.annual_package_offer_flexi_visible,
    ],
  );

  const nextSacredYearTitleLine = useMemo(() => irisYearLabelNoPeriod(autoRenewalYear), [autoRenewalYear]);

  /** Rich title for the on-record cycle (package name + Iris year for that window). */
  const lastAnnualCycleDisplayName = useMemo(() => {
    if (!lap?.start_date && !lap?.end_date) return '';
    const raw = (lap.program_label || '').trim();
    const pkgName = (pkg.program_name || '').trim();
    const programBit =
      raw && !/^annual program$/i.test(raw)
        ? raw
        : pkgName && !/^no active package$/i.test(pkgName)
          ? pkgName
          : 'Divine Iris Home Coming';
    const lapEndStr = (lap.end_date || '').trim().slice(0, 10);
    const completed = lapEndStr.length === 10 && lapEndStr < todayYmd;
    const ijYear = Math.min(12, Math.max(1, Number(homeData?.iris_journey?.year) || autoRenewalYear));
    const nameYear = completed ? Math.max(1, ijYear - 1) : ijYear;
    return `${programBit} · ${irisYearLabelNoPeriod(nameYear)}`;
  }, [lap, pkg.program_name, homeData?.iris_journey?.year, autoRenewalYear, todayYmd]);

  /**
   * Prior annual window vs the locked displayed cycle (ledger archive preferred — carries home_coming_sessions).
   */
  const priorPeriodMeta = useMemo(() => {
    const cStart = (cycleDisplayStart || '').trim().slice(0, 10);
    if (!/^\d{4}-\d{2}-\d{2}$/.test(cStart)) return null;
    const ledger = Array.isArray(homeData?.annual_period_ledger) ? homeData.annual_period_ledger : [];
    let best = null;
    let bestEnd = '';
    for (const e of ledger) {
      if (!e || typeof e !== 'object') continue;
      const end = String(e.end_date || '').slice(0, 10);
      if (end.length === 10 && end < cStart) {
        if (!best || end > bestEnd) {
          best = e;
          bestEnd = end;
        }
      }
    }
    if (best) return { kind: 'ledger', ...best };
    if (lapEndYmd.length === 10 && lapEndYmd < cStart && lapStartYmd.length === 10) {
      const ijY = Math.min(12, Math.max(1, Number(homeData?.iris_journey?.year) || autoRenewalYear));
      return {
        kind: 'lap',
        start_date: lapStartYmd,
        end_date: lapEndYmd,
        iris_year_at_archive: Math.max(1, ijY - 1),
        annual_program: lap?.program_label || null,
        home_coming_sessions: [],
      };
    }
    if (lapEnded && lapStartYmd.length === 10 && lapEndYmd.length === 10) {
      const ijY = Math.min(12, Math.max(1, Number(homeData?.iris_journey?.year) || autoRenewalYear));
      return {
        kind: 'lap',
        start_date: lapStartYmd,
        end_date: lapEndYmd,
        iris_year_at_archive: Math.max(1, ijY - 1),
        annual_program: lap?.program_label || null,
        home_coming_sessions: [],
      };
    }
    return null;
  }, [
    cycleDisplayStart,
    homeData?.annual_period_ledger,
    homeData?.iris_journey?.year,
    lapEndYmd,
    lapStartYmd,
    lapEnded,
    lap?.program_label,
    autoRenewalYear,
  ]);

  const useCollapsiblePriorSacredCycle = Boolean(membershipCycleDatesLocked && priorPeriodMeta);

  const priorPillarFirstDates = useMemo(
    () => earliestAvailedYmdByPillar(priorPeriodMeta?.home_coming_sessions),
    [priorPeriodMeta?.home_coming_sessions],
  );

  const priorCycleYearLabel = useMemo(() => {
    if (!priorPeriodMeta) return '';
    let y = priorPeriodMeta.iris_year_at_archive;
    if (typeof y !== 'number' || y < 1 || y > 12) {
      y = Math.max(1, autoRenewalYear - 1);
    }
    return irisYearLabelNoPeriod(y).replace(/\s+/g, ' ').trim();
  }, [priorPeriodMeta, autoRenewalYear]);

  /** Opening lines: renewal season vs in-journey vs Year 1 welcome. */
  const irisWelcomeLeadEl = useMemo(() => {
    const ij = homeData?.iris_journey;
    const y = Math.min(12, Math.max(1, Number(ij?.year) || 1));
    const strip = (t) => String(t || '').replace(/\.\s*$/, '').trim();
    const currentClean = strip(ij?.label) || irisYearLabelNoPeriod(y);
    const namePrefix = memberFirstName ? (
      <>
        <span className="font-semibold text-[#4c1d95]">{memberFirstName}</span>,{' '}
      </>
    ) : null;
    const renewLbl = irisYearLabelNoPeriod(autoRenewalYear);

    if (isRenewalSeason) {
      if (membershipCycleDatesLocked) {
        return (
          <>
            {namePrefix}
            thank you for walking your Sacred Home path. Your renewal for{' '}
            <span className="font-semibold text-[#4c1d95]">{renewLbl}</span> is recorded — your current cycle dates are set below
            and your payment schedule reflects checkout, even while Sacred Exchange catches up.
          </>
        );
      }
      return (
        <>
          {namePrefix}
          thank you for walking your Sacred Home path. Your previous annual window on file has completed or is in its
          renewal season; the path continues with <span className="font-semibold text-[#4c1d95]">{renewLbl}</span>.
          Anchor your next start date below — we show the matching bundle end date for that cycle, then you can checkout
          in one step when you feel a full yes.
        </>
      );
    }

    if (y >= 2) {
      const prevClean = irisYearLabelNoPeriod(y - 1);
      return (
        <>
          {namePrefix}
          thank you for completing {prevClean}. Your presence weaves luminous thread through this lineage;
          we hold you softly as you continue into sacred growth and higher becoming with {currentClean}.
        </>
      );
    }

    return (
      <>
        {namePrefix}
        thank you for choosing Divine Iris as you step into {currentClean}. Your presence weaves luminous
        thread through this lineage; we hold you softly as you deepen into sacred growth and higher becoming.
      </>
    );
  }, [homeData?.iris_journey, memberFirstName, isRenewalSeason, autoRenewalYear, membershipCycleDatesLocked]);

  useEffect(() => {
    if (!homeData || prefsInitDone.current) return;
    prefsInitDone.current = true;
    const p = homeData.annual_package_offer_prefs;
    const allowed = new Set(
      ALL_PAY_MODES.filter((row) => isAnnualOfferPayModeEnabled(homeData, row.value)).map((r) => r.value),
    );
    if (p && typeof p === 'object') {
      if (p.desired_start_date) {
        const raw = String(p.desired_start_date).slice(0, 10);
        setDesiredStart(/^\d{4}-\d{2}-\d{2}$/.test(raw) ? anchorHomeComingBundleStartYmd(raw) : raw);
      } else {
        const rawNext = homeComingThirdOfMonthAfterCurrentCalendarMonth(localCalendarTodayYmd());
        const ymd =
          /^\d{4}-\d{2}-\d{2}$/.test(rawNext) ? anchorHomeComingBundleStartYmd(rawNext) : '';
        if (ymd) setDesiredStart(ymd);
      }
      if (p.payment_mode) {
        const pm = String(p.payment_mode).trim();
        setPaymentMode(allowed.has(pm) ? pm : 'full');
      }
      if (p.emi_notes) setEmiNotes(String(p.emi_notes));
      if (p.participation_mode === 'offline' || p.participation_mode === 'online') {
        setAnnualParticipationMode(p.participation_mode);
      }
    } else {
      const rawNext = homeComingThirdOfMonthAfterCurrentCalendarMonth(localCalendarTodayYmd());
      const ymd = /^\d{4}-\d{2}-\d{2}$/.test(rawNext) ? anchorHomeComingBundleStartYmd(rawNext) : '';
      if (ymd) setDesiredStart(ymd);
    }
    setPrefsLoaded(true);
  }, [homeData]);

  /** Next advertised batch: 3rd of the calendar month after the member's current local month (renewal / no active cycle). */
  useEffect(() => {
    if (!prefsLoaded || !homeData) return;
    if (!(showNextSacredHomeEnrollment && !membershipCycleDatesLocked)) return;
    const todayLocal = localCalendarTodayYmd();
    const rawSuggested = homeComingThirdOfMonthAfterCurrentCalendarMonth(todayLocal);
    const suggested =
      /^\d{4}-\d{2}-\d{2}$/.test(rawSuggested) ? anchorHomeComingBundleStartYmd(rawSuggested) : '';
    if (!/^\d{4}-\d{2}-\d{2}$/.test(suggested)) return;
    setDesiredStart((prev) => {
      const anchoredPrev =
        prev && /^\d{4}-\d{2}-\d{2}$/.test(prev) ? anchorHomeComingBundleStartYmd(prev) : prev;
      if (!anchoredPrev || !/^\d{4}-\d{2}-\d{2}$/.test(anchoredPrev)) return suggested;
      if (anchoredPrev < todayLocal) return suggested;
      if (anchoredPrev.slice(0, 7) === todayLocal.slice(0, 7)) return suggested;
      return anchoredPrev;
    });
  }, [prefsLoaded, homeData, showNextSacredHomeEnrollment, membershipCycleDatesLocked]);

  useEffect(() => {
    if (paymentMode !== 'emi_flexi') setFlexiAmountInput('');
  }, [paymentMode]);

  useEffect(() => {
    if (!prefsLoaded || !homeData) return;
    const allowed = new Set(visiblePayModes.map((m) => m.value));
    if (!allowed.has(paymentMode)) {
      setPaymentMode('full');
    }
  }, [prefsLoaded, homeData, visiblePayModes, paymentMode]);

  useEffect(() => {
    if (!homeData || !prefsLoaded || !membershipCycleDatesLocked) return;
    if (portalHomeComingPaidInfo) return;
    if (/^\d{4}-\d{2}-\d{2}$/.test(recordStartYmd)) {
      const anchored = anchorHomeComingBundleStartYmd(recordStartYmd);
      setDesiredStart((prev) => (prev === anchored ? prev : anchored));
    }
  }, [homeData, prefsLoaded, membershipCycleDatesLocked, recordStartYmd, portalHomeComingPaidInfo]);

  useEffect(() => {
    if (!pinnedProgram || !baseCurrency) {
      setQuote(null);
      return;
    }
    setQuoteLoading(true);
    const tierIdx = pickTierIndexForDashboard(pinnedProgram, true);
    const tiers = pinnedProgram.duration_tiers || [];
    const params = {
      program_id: pinnedProgram.id,
      currency: baseCurrency,
      family_count: 0,
      booker_joins: true,
      home_coming_catalog: true,
    };
    if (pinnedProgram.is_flagship && tiers.length > 0 && tierIdx != null) {
      params.tier_index = tierIdx;
    }
    axios
      .get(`${getApiUrl()}/student/dashboard-quote`, {
        params,
        withCredentials: true,
        headers: getAuthHeaders(),
      })
      .then((r) => setQuote(r.data))
      .catch(() => setQuote(null))
      .finally(() => setQuoteLoading(false));
  }, [pinnedProgram, baseCurrency]);

  const persistPrefs = useCallback(() => {
    if (!homeData?.client_id) return;
    axios
      .put(
        `${getApiUrl()}/student/annual-package-offer-preferences`,
        {
          desired_start_date: desiredStart,
          payment_mode: paymentMode,
          participation_mode: annualParticipationMode,
          emi_notes: emiNotes,
        },
        { withCredentials: true },
      )
      .catch(() => {})
  }, [homeData?.client_id, desiredStart, paymentMode, annualParticipationMode, emiNotes]);

  useEffect(() => {
    if (!prefsLoaded) return;
    if (prefsSaveTimer.current) clearTimeout(prefsSaveTimer.current);
    prefsSaveTimer.current = setTimeout(() => {
      persistPrefs();
    }, 700);
    return () => {
      if (prefsSaveTimer.current) clearTimeout(prefsSaveTimer.current);
    };
  }, [desiredStart, paymentMode, annualParticipationMode, emiNotes, prefsLoaded, persistPrefs]);

  const totalRaw = Number(quote?.total ?? 0);

  /** True when Client Garden /student/home financials fee is in the same currency as the member's portal hub (e.g. AED). */
  const crmSubscriptionFeeMatchesHub = useMemo(() => {
    const n = Number(fin.total_fee || 0);
    if (n <= 0) return false;
    const fc = (fin.currency || '').toString().trim().toLowerCase();
    const hub = (baseCurrency || '').toString().trim().toLowerCase();
    return Boolean(fc && hub && fc === hub);
  }, [fin.total_fee, fin.currency, baseCurrency]);

  /** When admin set fee + mode on the subscriber row (renewal), member pricing should follow CRM — not the generic program quote. */
  const preferCrmAnnualScheduleTotal = useMemo(() => {
    const onFileFee = Number(fin.total_fee || 0);
    if (!crmSubscriptionFeeMatchesHub || onFileFee <= 0) return false;
    const pm = String(fin.payment_mode || '').trim();
    if (pm === 'EMI' && emis.length > 0) return true;
    if (pm === 'Full Paid') return true;
    if (isRenewalSeason) return true;
    return false;
  }, [crmSubscriptionFeeMatchesHub, fin.total_fee, fin.payment_mode, emis.length, isRenewalSeason]);

  /** During renewal, Sacred Exchange (not catalog list) is the source of truth for the headline total. */
  const showRenewalSacredExchangePrice = useMemo(
    () =>
      isRenewalSeason &&
      crmSubscriptionFeeMatchesHub &&
      Number(fin.total_fee || 0) > 0,
    [isRenewalSeason, crmSubscriptionFeeMatchesHub, fin.total_fee],
  );

  /** Divine Cart / checkout line title — pinned program title is AWRP; members expect Home Coming wording + Iris year. */
  const homeComingCartTitle = useMemo(() => {
    if (!homeData) return 'Home Coming Annual Program';
    const yCurrent = Math.min(12, Math.max(1, Number(homeData.iris_journey?.year) || 1));
    const yRenew = Math.min(
      12,
      Math.max(1, Number(homeData.renewal_entering_iris_year) || yCurrent),
    );
    const y = isRenewalSeason || showNextSacredHomeEnrollment ? yRenew : yCurrent;
    return `Home Coming Annual Program · Year ${y}`;
  }, [homeData, isRenewalSeason, showNextSacredHomeEnrollment]);

  /** Basis for splitting the payment schedule: admin CRM fee when set for renewal/EMI, else program quote, else catalog. */
  const scheduleSplitTotal = useMemo(() => {
    const onFileFee = Number(fin.total_fee || 0);
    if (preferCrmAnnualScheduleTotal && onFileFee > 0) {
      return onFileCourtesyAdjustedTotal != null ? onFileCourtesyAdjustedTotal : onFileFee;
    }
    if (totalRaw > 0) return totalRaw;
    if (crmSubscriptionFeeMatchesHub && onFileFee > 0) {
      return onFileCourtesyAdjustedTotal != null ? onFileCourtesyAdjustedTotal : onFileFee;
    }
    if (preferOnFilePackagePricing && onFileFee > 0) {
      return onFileCourtesyAdjustedTotal != null ? onFileCourtesyAdjustedTotal : onFileFee;
    }
    if (
      catalogCourtesyBreakdown != null &&
      Number(catalogCourtesyBreakdown.finalNum) > 0
    ) {
      return Number(catalogCourtesyBreakdown.finalNum);
    }
    if (
      primaryCatalogFallbackCourtesyBreakdown != null &&
      Number(primaryCatalogFallbackCourtesyBreakdown.finalNum) > 0
    ) {
      return Number(primaryCatalogFallbackCourtesyBreakdown.finalNum);
    }
    if (catalogAmountHub != null && Number(catalogAmountHub) > 0) return Number(catalogAmountHub);
    return 0;
  }, [
    preferCrmAnnualScheduleTotal,
    preferOnFilePackagePricing,
    crmSubscriptionFeeMatchesHub,
    fin.total_fee,
    onFileCourtesyAdjustedTotal,
    totalRaw,
    catalogAmountHub,
    catalogCourtesyBreakdown,
    primaryCatalogFallbackCourtesyBreakdown,
  ]);

  const scheduleBasisYmd = useMemo(() => {
    const ymd = (s) => {
      const v = String(s || '').trim().slice(0, 10);
      return /^\d{4}-\d{2}-\d{2}$/.test(v) ? v : '';
    };
    const ds = ymd(desiredStart);
    const cds = ymd(cycleDisplayStart);
    const rs = ymd(recordStartYmd);
    const re = ymd(recordEndYmd);
    if (ds && re && ds > re) return anchorHomeComingBundleStartYmd(ds);
    if (cds) return anchorHomeComingBundleStartYmd(cds);
    if (ds) return anchorHomeComingBundleStartYmd(ds);
    if (rs) return anchorHomeComingBundleStartYmd(rs);
    return '';
  }, [cycleDisplayStart, desiredStart, recordStartYmd, recordEndYmd]);

  const useCrmMonthlyEmiSchedule = useMemo(() => {
    if (paymentMode !== 'emi_monthly') return false;
    if (String(fin.payment_mode || '').trim() !== 'EMI') return false;
    if (emis.length === 0) return false;
    return preferCrmAnnualScheduleTotal;
  }, [paymentMode, fin.payment_mode, emis.length, preferCrmAnnualScheduleTotal]);

  const paymentScheduleRows = useMemo(() => {
    let rows;
    if (useCrmMonthlyEmiSchedule) {
      rows = buildPaymentScheduleRowsFromCrmEmis(emis, scheduleBasisYmd, paymentMode);
      if (onFileCourtesyAdjustedTotal != null && Number(fin.total_fee || 0) > 0) {
        rows = scaleScheduleRowsToTarget(rows, onFileCourtesyAdjustedTotal);
      }
    } else {
      rows = buildPaymentScheduleRows(paymentMode, scheduleSplitTotal, scheduleBasisYmd, durationMonths);
    }
    if (paymentMode === 'emi_monthly') {
      rows = applyHomeComingCrmDueAdvanceShift(rows, emis);
    }
    return rows;
  }, [
    useCrmMonthlyEmiSchedule,
    emis,
    onFileCourtesyAdjustedTotal,
    fin.total_fee,
    paymentMode,
    scheduleSplitTotal,
    scheduleBasisYmd,
    durationMonths,
  ]);

  /**
   * When ``subscription.emis`` lags paid Razorpay/Stripe/manual-receipt orders, match each unpaid CRM
   * installment row to a paid Home Coming order by amount (same tolerance as the backend EMI sync).
   */
  const portalEmiInstallmentMatches = useMemo(() => {
    const hub = String(baseCurrency || '').toLowerCase();
    const list = Array.isArray(portalOrders) ? portalOrders : [];
    const baseCandidates = list.filter((o) => {
      if (!o || o.is_free) return false;
      if (String(o.payment_status || '').toLowerCase() !== 'paid') return false;
      const amt = Number(o.amount);
      if (!Number.isFinite(amt) || amt <= 0) return false;
      const cur = String(o.currency || '').toLowerCase();
      if (hub && cur && cur !== hub) return false;
      return true;
    });
    const strictCandidates = baseCandidates.filter((o) =>
      String(o.item_title || '').toLowerCase().includes('home coming'),
    );
    // Legacy rows may carry AWRP title for Home Coming EMI transactions.
    const candidates = strictCandidates.length > 0 ? strictCandidates : baseCandidates;
    candidates.sort((a, b) => {
      const ta = new Date(a.created_at || a.updated_at || 0).getTime();
      const tb = new Date(b.created_at || b.updated_at || 0).getTime();
      return ta - tb;
    });
    const map = new Map();
    const used = new Set();
    for (const row of paymentScheduleRows) {
      if (typeof row.n !== 'number' || Number.isNaN(row.n)) continue;
      if (!String(row.key || '').startsWith('emi-')) continue;
      const emiCrm = emiByNumber.get(row.n);
      if (emiCrm && String(emiCrm.status || '').toLowerCase() === 'paid') continue;
      const rowAmt = Number(row.amount);
      if (!Number.isFinite(rowAmt)) continue;
      const tol = Math.max(2, Math.abs(rowAmt) * 0.025);
      for (const o of candidates) {
        const oid = String(o.id || o.stripe_session_id || '').trim();
        if (!oid || used.has(oid)) continue;
        if (Math.abs(Number(o.amount) - rowAmt) > tol) continue;
        used.add(oid);
        const when = String(o.created_at || o.updated_at || '');
        map.set(row.key, {
          paidAtYmd: when.slice(0, 10),
          stripeSessionId: String(o.stripe_session_id || '').trim(),
          paymentMethod: String(
            o.india_payment_method ||
              o.payment_method ||
              o.payment_provider ||
              (o.stripe_session_id ? 'stripe' : 'stripe'),
          ).trim(),
        });
        break;
      }
    }
    return map;
  }, [portalOrders, baseCurrency, paymentScheduleRows, emiByNumber]);

  const paymentScheduleNumericTotal = useMemo(() => {
    let s = 0;
    for (const row of paymentScheduleRows) {
      if (row.amount == null || row.amountDisplay != null || Number.isNaN(Number(row.amount))) continue;
      const k = String(row.key || '');
      if (k.startsWith('emi-') || k === 'pay-full' || k === 'flex-ref') {
        s += Number(row.amount);
      }
    }
    return Math.round(s * 100) / 100;
  }, [paymentScheduleRows]);

  const paymentSchedulePaidTotal = useMemo(() => {
    let paid = 0;
    for (const row of paymentScheduleRows) {
      const amt = Number(row.amount);
      if (row.amount == null || row.amountDisplay != null || Number.isNaN(amt)) continue;
      const k = String(row.key || '');
      if (!(k.startsWith('emi-') || k === 'pay-full' || k === 'flex-ref')) continue;
      const n = Number(row.n);
      const emiRow = Number.isFinite(n) ? emiByNumber.get(n) : null;
      const portalPayFullMatchesRow =
        portalHomeComingPaidInfo &&
        paymentMode === 'full' &&
        k === 'pay-full' &&
        Math.abs(Number(portalHomeComingPaidInfo.amount) - amt) <= Math.max(2, Math.abs(amt) * 0.025);
      const crmEmiPaid = emiRow && String(emiRow.status || '').toLowerCase() === 'paid';
      const portalEmiMatch = portalEmiInstallmentMatches.get(k) || null;
      const syntheticPaidRow = Boolean(
        (portalPayFullMatchesRow && !crmEmiPaid) || (portalEmiMatch && !crmEmiPaid),
      );
      if (crmEmiPaid || syntheticPaidRow) paid += amt;
    }
    return Math.round(paid * 100) / 100;
  }, [
    paymentScheduleRows,
    emiByNumber,
    portalHomeComingPaidInfo,
    paymentMode,
    portalEmiInstallmentMatches,
  ]);

  const paymentScheduleEmiRowCount = useMemo(
    () => paymentScheduleRows.filter((r) => String(r.key || '').startsWith('emi-')).length,
    [paymentScheduleRows],
  );

  /** Payable total: live quote and/or catalog offer amount. */
  const hasPayableCheckoutTotal = totalRaw > 0 || scheduleSplitTotal > 0;
  const canContinueToCheckout = Boolean(
    pinnedProgram &&
      !quoteLoading &&
      (hasPayableCheckoutTotal || quote?.included_in_annual_package === true),
  );

  const scheduleTitle = useMemo(() => {
    if (paymentMode === 'full') return 'PAYMENT SCHEDULE · PAY IN FULL';
    if (paymentMode === 'emi_flexi') return 'PAYMENT SCHEDULE · FLEXI';
    const n = paymentScheduleRows.length;
    return `PAYMENT SCHEDULE · ${n} INSTALLMENT${n === 1 ? '' : 'S'}`;
  }, [paymentMode, paymentScheduleRows.length]);

  const memberHasStripe = useMemo(() => {
    const pm = homeData?.payment_methods;
    if (!pm || !Array.isArray(pm) || pm.length === 0) return true;
    return pm.some((m) => String(m).toLowerCase() === 'stripe');
  }, [homeData?.payment_methods]);

  /** Single-click Pay on schedule: jump straight to Stripe Checkout when subscription allows Stripe and India-only is not chosen. */
  const useAutoStripeCheckout = useMemo(() => {
    if (!memberHasStripe) return false;
    const pref = String(homeData?.preferred_payment_method || '').trim().toLowerCase();
    const india = String(homeData?.client_india_pricing?.india_payment_method || '').trim().toLowerCase();
    const mode = String(fin.payment_mode || '').trim().toLowerCase();
    const raw = pref || india || mode;
    const indiaOnly =
      raw &&
      (raw.includes('gpay') ||
        raw.includes('upi') ||
        raw.includes('bank') ||
        raw === 'manual' ||
        raw.includes('exly') ||
        raw.includes('neft') ||
        raw.includes('cash'));
    if (indiaOnly) return false;
    if (schedulePayTag === 'Stripe') return true;
    if (!raw) return true;
    return raw.includes('stripe') || raw === 'card' || raw === 'cards';
  }, [
    memberHasStripe,
    schedulePayTag,
    homeData?.preferred_payment_method,
    homeData?.client_india_pricing?.india_payment_method,
    fin.payment_mode,
  ]);

  /** Minimal valid booker row so Divine Cart autoPay can pass validateAndProceed without empty placeholders. */
  const buildSacredHomeQuickPayParticipants = useCallback(
    (_program) => {
      const ud = homeData?.user_details || {};
      const name =
        (ud.full_name || user?.full_name || user?.name || '').trim() || 'Sacred Home member';
      const hub = (baseCurrency || 'inr').toLowerCase();
      const country =
        String(ud.country || user?.country || '').trim() ||
        (hub === 'inr' ? 'IN' : hub === 'aed' ? 'AE' : hub === 'usd' ? 'US' : 'AE');
      const city = String(ud.city || user?.city || '').trim() || '—';
      const state =
        String(ud.state || ud.city || user?.state || '—').trim() || '—';
      const attendance_mode = annualParticipationMode === 'offline' ? 'offline' : 'online';
      const email = String(user?.email || ud.email || homeData?.contact_email || '').trim();
      const gender = String(user?.gender || ud.gender || 'Other').trim() || 'Other';

      let phone = '';
      let phone_code = '';
      let whatsapp = '';
      let wa_code = '';
      const nationalDigits = String(ud.phone || user?.phone || '').replace(/\D/g, '');
      let pc = String(ud.phone_code || user?.phone_code || '').trim();
      if (pc && !pc.startsWith('+')) pc = `+${pc.replace(/\D/g, '')}`;
      if (nationalDigits && pc) {
        phone = nationalDigits;
        phone_code = pc;
        whatsapp = nationalDigits;
        wa_code = pc;
      } else {
        const raw = String(ud.phone || user?.phone || '')
          .trim()
          .replace(/\s+/g, '');
        if (raw) {
          const sp = splitPhoneForCart(raw);
          phone = sp.phone;
          phone_code = sp.phone_code;
          whatsapp = sp.whatsapp || sp.phone;
          wa_code = sp.wa_code || sp.phone_code;
        }
      }

      return [
        {
          name,
          relationship: 'Myself',
          age: '',
          gender,
          country,
          city,
          state,
          attendance_mode,
          notify: attendance_mode === 'online',
          email,
          phone,
          phone_code,
          wa_code,
          whatsapp,
          is_first_time: false,
          referral_source: '',
          referred_by_email: '',
          referred_by_name: '',
          has_referral: false,
        },
      ];
    },
    [homeData?.user_details, homeData?.contact_email, user, baseCurrency, annualParticipationMode],
  );

  /** Amount sent to Divine Cart / Stripe for Home Coming — one installment for EMI, never the full bundle total. */
  const resolveHomeComingQuotedTotal = useCallback(
    (clickedRow) => {
      if (paymentMode === 'full') {
        return scheduleSplitTotal;
      }
      if (paymentMode === 'emi_flexi') {
        if (clickedRow && typeof clickedRow.amount === 'number' && clickedRow.amount > 0) {
          return clickedRow.amount;
        }
        return scheduleSplitTotal;
      }
      if (
        clickedRow &&
        typeof clickedRow.amount === 'number' &&
        !Number.isNaN(clickedRow.amount) &&
        clickedRow.amount > 0
      ) {
        return clickedRow.amount;
      }
      const firstInst = paymentScheduleRows.find(
        (r) =>
          String(r.key || '').startsWith('emi-') &&
          typeof r.amount === 'number' &&
          !Number.isNaN(r.amount) &&
          r.amount > 0,
      );
      return firstInst ? firstInst.amount : scheduleSplitTotal;
    },
    [paymentMode, scheduleSplitTotal, paymentScheduleRows],
  );

  const resolveHomeComingPayInstallmentN = useCallback(
    (clickedRow) => {
      if (paymentMode !== 'emi_monthly' && paymentMode !== 'emi_quarterly' && paymentMode !== 'emi_yearly') {
        return null;
      }
      if (typeof clickedRow?.n === 'number' && !Number.isNaN(clickedRow.n) && clickedRow.n >= 1) {
        return clickedRow.n;
      }
      return 1;
    },
    [paymentMode],
  );

  /** Optional `clickedRow`: schedule row when using Pay · Stripe on a specific installment. */
  const goCheckout = (clickedRow = null) => {
    const autoQs = useAutoStripeCheckout ? '?autoPay=1' : '';
    let rowForQuote = clickedRow;
    if (
      pinnedProgram &&
      paymentMode === 'emi_flexi' &&
      clickedRow &&
      String(clickedRow.key) === 'flex-a'
    ) {
      const trimmed = String(flexiAmountInput).replace(/,/g, '').trim();
      const n = Number(trimmed);
      if (!Number.isFinite(n) || n <= 0) {
        toast({
          title: 'Enter your amount',
          description: `Type how much you wish to pay (${(baseCurrency || 'inr').toUpperCase()}), then tap Pay again.`,
          variant: 'destructive',
        });
        return;
      }
      rowForQuote = { ...clickedRow, amount: Math.round(n * 100) / 100 };
    }
    if (pinnedProgram) {
      const tierIdx = pickTierIndexForDashboard(pinnedProgram, true) ?? 0;
      const participants = buildSacredHomeQuickPayParticipants(pinnedProgram);
      const quoted = resolveHomeComingQuotedTotal(rowForQuote);
      const payInstallmentN = resolveHomeComingPayInstallmentN(rowForQuote);
      const schedulePreview =
        paymentMode !== 'full' && paymentMode !== 'emi_flexi'
          ? paymentScheduleRows
              .filter((r) => r.key !== 'flex-a' && r.key !== 'flex-ref')
              .slice(0, 36)
              .map((r) => ({
                n: r.n,
                due: r.due || null,
                amount: typeof r.amount === 'number' ? r.amount : null,
              }))
          : null;
      flushSync(() => {
        const meta = {
          fromAnnualOfferPage: true,
          homeComingCartTitle,
          /** Roster pricing must treat the booker as package-covered so subtotal is not the full tier (only `homeComingQuotedTotal` is payable). */
          annualIncluded: true,
          annualOfferParticipation: annualParticipationMode,
          ...(quoted > 0 ? { homeComingQuotedTotal: quoted } : {}),
          annualOfferPaymentMode: paymentMode,
          ...(schedulePreview && schedulePreview.length ? { annualOfferSchedulePreview: schedulePreview } : {}),
        };
        if (payInstallmentN != null) {
          meta.homeComingPayInstallmentN = payInstallmentN;
        }
        syncProgramLineItem(pinnedProgram, tierIdx, participants, meta);
      });
      navigate(`/dashboard/combined-checkout${autoQs}`);
      return;
    }
    /* Illustrative schedule or no catalog pin: still send members somewhere to pay (combined-checkout needs a line item). */
    navigate('/dashboard/financials');
  };

  if (loading) {
    return (
      <section
        className="w-full max-w-[68rem] mx-auto pl-4 pr-6 sm:pr-8 md:pr-10 lg:pr-12 py-6 md:py-8"
        data-testid="annual-package-purchase-loading"
      >
        <div className="rounded-[28px] border border-[rgba(160,100,240,0.2)] bg-white/45 backdrop-blur-xl px-5 py-16 flex flex-col items-center justify-center gap-3 text-slate-600 shadow-[0_4px_48px_rgba(140,60,220,0.1)]">
          <Clock className="h-8 w-8 animate-spin text-[#5D3FD3]" aria-hidden />
          <p className="text-sm font-light tracking-wide">Gathering your sanctuary…</p>
        </div>
      </section>
    );
  }

  if (!homeData) {
    return (
      <section
        className="w-full max-w-[68rem] mx-auto pl-4 pr-6 sm:pr-8 md:pr-10 lg:pr-12 py-6 md:py-8"
        data-testid="annual-package-purchase-error"
      >
        <div className="rounded-[28px] border border-red-200/90 bg-white/85 backdrop-blur-xl px-5 py-12 flex flex-col items-center justify-center gap-4 text-center text-slate-800 shadow-[0_4px_48px_rgba(140,60,220,0.08)]">
          <p className="text-sm font-medium text-[#5b21b6] max-w-md">We could not load your Sacred Home data from the server.</p>
          <p className="text-xs text-slate-600 max-w-md">
            Try again, or use the circle menu at the bottom-left for Financials · Sign out.
          </p>
          <Button type="button" className="bg-[#5D3FD3]" onClick={() => refresh()}>
            Retry
          </Button>
        </div>
      </section>
    );
  }

  const glassOuter =
    'rounded-[28px] border border-[rgba(160,100,240,0.2)] bg-white/45 backdrop-blur-xl shadow-[0_4px_48px_rgba(140,60,220,0.1)] overflow-hidden';

  const glassInset =
    'rounded-2xl border border-[rgba(160,100,240,0.15)] bg-gradient-to-br from-white/55 to-[rgba(250,245,255,0.45)] backdrop-blur-md px-4 py-4 md:px-5 md:py-5';

  return (
    <>
    <div
      className="relative w-full min-h-[calc(100vh-6rem)] animate-[fadeSlideUpHc_0.85s_ease-out_both]"
      data-testid="annual-package-purchase-page"
    >
      <section className="w-full max-w-[68rem] mx-auto pl-4 pr-6 sm:pr-8 md:pr-10 lg:pr-12 pb-12 md:pb-16 pt-4 md:pt-6">
        <div className={glassOuter}>
          <div className="pointer-events-none h-px w-full bg-gradient-to-r from-transparent via-[#c084fc]/80 via-[#f5c840]/70 via-[#f9a8d4]/80 to-transparent shrink-0" />

          <div className="mb-5 md:mb-6 mt-6 md:mt-7 flex flex-col items-center text-center px-3 sm:px-5">
            <DashboardUpcomingProgramsIrisBloom />
            <h1
              id="divine-iris-home-coming-title"
              className="font-[family-name:'Cinzel',serif] mt-2 px-2 text-xl font-bold tracking-[0.16em] uppercase text-[#3b0764] drop-shadow-sm md:text-2xl lg:text-[1.85rem]"
              data-testid="divine-iris-home-coming-title"
            >
              Divine Iris Home Coming
            </h1>
            <p
              className="mt-2.5 text-sm sm:text-base text-[rgba(60,25,115,0.68)] font-medium tracking-[0.04em]"
              data-testid="divine-iris-home-coming-subtitle"
            >
              {subtitleFourPrograms}
            </p>
            <div className="mt-6 w-full flex justify-center md:justify-end lg:justify-end md:pl-2 lg:pl-3 md:pr-[min(11vw,8.5rem)] lg:pr-[min(13vw,10rem)]">
              <div
                className="w-full max-w-[min(46rem,100%)] md:max-w-[min(54rem,calc(100%-0.25rem))] lg:max-w-[min(60rem,calc(100%-max(7rem,8vw)))] xl:max-w-[min(62rem,calc(100%-max(6.5rem,7vw)))] rounded-[22px] border border-[rgba(196,168,252,0.45)] bg-gradient-to-br from-[#faf8ff]/96 via-[#fffefd]/93 to-[#f5f0ff]/93 px-4 py-5 sm:px-5 sm:py-6 md:px-6 md:py-6 text-center shadow-[0_12px_48px_rgba(124,58,237,0.12)] backdrop-blur-sm"
                data-testid="home-coming-welcome-banner"
              >
              <div className="flex flex-col items-center gap-4 sm:gap-5">
                <Sparkles
                  className="h-9 w-9 shrink-0 text-[#a855f7] opacity-[0.92] drop-shadow-[0_0_12px_rgba(168,85,247,0.35)]"
                  aria-hidden
                />
                <div className="w-full min-w-0 space-y-4">
                  {(lap?.start_date || lap?.end_date) && !useCollapsiblePriorSacredCycle ? (
                    <div
                      className="rounded-[1.35rem] border-[3px] border-[rgba(124,58,237,0.45)] bg-gradient-to-br from-white via-[#faf8ff] to-violet-50/90 px-6 py-8 sm:px-10 sm:py-10 md:px-12 md:py-11 shadow-[0_16px_56px_rgba(91,33,182,0.18)] ring-1 ring-violet-200/60"
                      data-testid="home-coming-current-cycle-highlight"
                    >
                      <p className="text-xs sm:text-sm uppercase tracking-[0.2em] text-[rgba(100,55,155,0.62)] font-bold mb-4">
                        {onRecordCycleBannerTitle}
                      </p>
                      <p
                        className="text-xl sm:text-2xl md:text-[1.85rem] lg:text-3xl font-bold text-[#3b0764] leading-snug tracking-tight max-w-[42rem] mx-auto"
                        data-testid="last-annual-package-label"
                      >
                        {lastAnnualCycleDisplayName || lap.program_label || 'Annual program'}
                      </p>
                      <dl className="mt-8 flex flex-wrap justify-center gap-x-14 sm:gap-x-20 gap-y-6 tabular-nums">
                        <div>
                          <dt className="text-xs sm:text-sm uppercase tracking-[0.14em] text-[rgba(100,55,155,0.5)] mb-2.5 font-semibold">
                            Start date
                          </dt>
                          <dd className="text-2xl sm:text-3xl md:text-4xl lg:text-[2.65rem] font-bold text-[#3b0764] leading-none">
                            {lap.start_date ? formatDateDdMonYyyy(lap.start_date) : '—'}
                          </dd>
                        </div>
                        <div>
                          <dt className="text-xs sm:text-sm uppercase tracking-[0.14em] text-[rgba(100,55,155,0.5)] mb-2.5 font-semibold">
                            End date
                          </dt>
                          <dd className="text-2xl sm:text-3xl md:text-4xl lg:text-[2.65rem] font-bold text-[#3b0764] leading-none">
                            {lap.end_date ? formatDateDdMonYyyy(lap.end_date) : '—'}
                          </dd>
                        </div>
                      </dl>
                    </div>
                  ) : null}
                  <p
                    className="font-[family-name:'Playfair_Display',Georgia,serif] text-[17px] sm:text-lg leading-relaxed text-[#2e1067]/92"
                    data-testid="home-coming-welcome-lead"
                  >
                    {irisWelcomeLeadEl}
                  </p>
                  {showNextSacredHomeEnrollment ? (
                  <div className="rounded-2xl border border-[rgba(124,58,237,0.35)] bg-gradient-to-br from-violet-50/90 to-white/70 px-4 py-3.5 shadow-sm shadow-violet-200/30 space-y-3">
                      <>
                        {membershipCycleDatesLocked ? (
                          <>
                            <p className="text-[10px] uppercase tracking-[0.16em] text-[rgba(100,55,155,0.48)] font-semibold">
                              Current Sacred Home cycle
                            </p>
                            <div className="flex flex-col items-center justify-center gap-3">
                              <div className="min-w-0 max-w-full flex flex-col items-center">
                                <Label className="text-[10px] flex flex-col items-center gap-2 text-[rgba(70,35,125,0.65)] uppercase tracking-[0.12em] font-semibold leading-snug">
                                  <Calendar size={12} className="opacity-70 shrink-0" aria-hidden />
                                  <span className="text-center">
                                    <span className="block text-[10px] font-bold tracking-[0.14em] text-[rgba(100,55,155,0.45)] uppercase">
                                      {nextSacredYearTitleLine}
                                    </span>
                                    <span className="block normal-case tracking-normal text-[11px] font-medium text-[rgba(60,35,115,0.65)] mt-2 mx-auto max-w-none w-full max-w-xl">
                                      Your membership for this cycle is already paid. Dates below match your checkout and stay in
                                      sync with Sacred Exchange when your host updates the garden record.
                                    </span>
                                  </span>
                                </Label>
                              </div>
                            </div>
                            {cycleDisplayEnd ? (
                              <div className="rounded-xl border border-violet-200/60 bg-white/65 px-3 py-2.5 sm:px-4">
                                <p className="text-[12px] sm:text-[13px] font-bold uppercase tracking-[0.14em] text-[#3b0764] leading-snug text-center">
                                  {irisYearLabelNoPeriod(autoRenewalYear).replace(/\s+/g, ' ').trim().toUpperCase()}
                                </p>
                                <dl className="mt-3 flex flex-wrap justify-center gap-x-10 gap-y-3 sm:gap-x-12 tabular-nums">
                                  <div className="min-w-[7.5rem]">
                                    <dt className="text-[9px] uppercase tracking-[0.12em] text-[rgba(100,55,155,0.42)] mb-1 font-semibold">
                                      Cycle start
                                    </dt>
                                    <dd className="text-center text-[13px] sm:text-sm font-bold uppercase tracking-[0.08em] text-[#3b0764]">
                                      {formatDateDdMonYyyy(cycleDisplayStart).toUpperCase()}
                                    </dd>
                                  </div>
                                  <div className="min-w-[7.5rem]">
                                    <dt className="text-[9px] uppercase tracking-[0.12em] text-[rgba(100,55,155,0.42)] mb-1 font-semibold">
                                      Bundle end ({durationMonths} mo)
                                    </dt>
                                    <dd className="text-center text-[13px] sm:text-sm font-bold uppercase tracking-[0.08em] text-[#3b0764]">
                                      {formatDateDdMonYyyy(cycleDisplayEnd).toUpperCase()}
                                    </dd>
                                  </div>
                                </dl>
                              </div>
                            ) : (
                              <p className="text-[11px] text-[rgba(90,55,135,0.62)] italic max-w-none mx-auto">
                                Complete member profile dates are still loading — refresh in a moment if this stays empty.
                              </p>
                            )}
                            {!pinnedProgram ? (
                              <p className="text-[11px] text-amber-900/85 bg-amber-50/90 border border-amber-200/80 rounded-lg px-2.5 py-2 text-left sm:text-center">
                                Your host still needs to pin the Home Coming catalog program on Sacred Home — then checkout opens
                                here with your dates.
                              </p>
                            ) : null}
                          </>
                        ) : (
                          <>
                            <p className="text-[10px] uppercase tracking-[0.16em] text-[rgba(100,55,155,0.48)] font-semibold">
                              Next Sacred Home cycle
                            </p>
                            <div className="flex flex-col sm:flex-row flex-wrap items-center justify-center gap-3">
                              <div className="min-w-0 max-w-full flex flex-col items-center">
                                <Label className="text-[10px] flex flex-col items-center gap-2 text-[rgba(70,35,125,0.65)] uppercase tracking-[0.12em] font-semibold leading-snug">
                                  <Calendar size={12} className="opacity-70 shrink-0" aria-hidden />
                                  <span className="text-center">
                                    <span className="block text-[10px] font-bold tracking-[0.14em] text-[rgba(100,55,155,0.45)] uppercase">
                                      {nextSacredYearTitleLine}
                                    </span>
                                    <span className="block text-[10px] font-bold tracking-[0.14em] text-[rgba(100,55,155,0.45)] mt-1 uppercase">
                                      Anchor your membership start
                                    </span>
                                    <span className="block normal-case tracking-normal text-[11px] font-medium text-[rgba(60,35,115,0.65)] mt-1 mx-auto max-w-none w-full">
                                      Home Coming opens on the <strong>3rd</strong> of each month. While you are choosing
                                      your next cycle, we default the batch to the <strong>3rd of the next calendar month</strong>{' '}
                                      (e.g. in May → 3 June; in June → 3 July). Your Iris year still follows your Iris Garden
                                      path; your host can align dates in Iris Annual Abundance if something on record needs a
                                      gentle correction.
                                    </span>
                                  </span>
                                </Label>
                                {desiredStartInputLocked ? (
                                  <div
                                    className="h-10 mt-1.5 w-[11.75rem] max-w-full rounded-md border border-violet-200/90 bg-violet-50/80 mx-auto px-3 flex items-center justify-center text-[14px] font-medium tracking-[0.02em] text-violet-950"
                                    data-testid="annual-offer-start-date-hero"
                                    aria-label="Locked bundle start date"
                                  >
                                    {formatDateDdMonYyyy(desiredStart || cycleDisplayStart || todayYmd)}
                                  </div>
                                ) : (
                                  <Input
                                    type="date"
                                    className="h-10 mt-1.5 w-[11.75rem] max-w-full border-[rgba(160,80,220,0.22)] bg-white/75 mx-auto"
                                    value={desiredStart}
                                    onChange={(e) => setDesiredStart(e.target.value)}
                                    data-testid="annual-offer-start-date-hero"
                                  />
                                )}
                                {desiredStartInputLocked ? (
                                  <p className="text-[10px] text-[rgba(80,45,130,0.72)] mt-1.5 leading-snug max-w-md mx-auto">
                                    Start and bundle end are locked to your Sacred Exchange record after payment.
                                  </p>
                                ) : null}
                              </div>
                              {!desiredStartInputLocked ? (
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="sm"
                                  className="h-10 border-violet-200/90 text-violet-900 bg-white/70 hover:bg-violet-50 shrink-0"
                                  onClick={() => {
                                    const todayLocal = localCalendarTodayYmd();
                                    const raw = homeComingThirdOfMonthAfterCurrentCalendarMonth(todayLocal);
                                    const ymd =
                                      /^\d{4}-\d{2}-\d{2}$/.test(raw)
                                        ? anchorHomeComingBundleStartYmd(raw)
                                        : '';
                                    if (ymd) setDesiredStart(ymd);
                                  }}
                                >
                                  Default · 3rd next month
                                </Button>
                              ) : null}
                            </div>
                            {cycleDisplayEnd ? (
                              <div className="rounded-xl border border-violet-200/60 bg-white/65 px-3 py-2.5 sm:px-4">
                                <p className="text-[12px] sm:text-[13px] font-bold uppercase tracking-[0.14em] text-[#3b0764] leading-snug text-center">
                                  {irisYearLabelNoPeriod(autoRenewalYear).replace(/\s+/g, ' ').trim().toUpperCase()}
                                </p>
                                <dl className="mt-3 flex flex-wrap justify-center gap-x-10 gap-y-3 sm:gap-x-12 tabular-nums">
                                  <div className="min-w-[7.5rem]">
                                    <dt className="text-[9px] uppercase tracking-[0.12em] text-[rgba(100,55,155,0.42)] mb-1 font-semibold">
                                      New cycle start
                                    </dt>
                                    <dd className="text-center text-[13px] sm:text-sm font-bold uppercase tracking-[0.08em] text-[#3b0764]">
                                      {formatDateDdMonYyyy(cycleDisplayStart).toUpperCase()}
                                    </dd>
                                  </div>
                                  <div className="min-w-[7.5rem]">
                                    <dt className="text-[9px] uppercase tracking-[0.12em] text-[rgba(100,55,155,0.42)] mb-1 font-semibold">
                                      Bundle end ({durationMonths} mo)
                                    </dt>
                                    <dd className="text-center text-[13px] sm:text-sm font-bold uppercase tracking-[0.08em] text-[#3b0764]">
                                      {formatDateDdMonYyyy(cycleDisplayEnd).toUpperCase()}
                                    </dd>
                                  </div>
                                </dl>
                              </div>
                            ) : (
                              <p className="text-[11px] text-[rgba(90,55,135,0.62)] max-w-none mx-auto">
                                Pick a start date to see bundle end (30th of the month before the anniversary) and the
                                installment schedule (due on the 27th).
                              </p>
                            )}
                            {!pinnedProgram ? (
                              <p className="text-[11px] text-amber-900/85 bg-amber-50/90 border border-amber-200/80 rounded-lg px-2.5 py-2 text-left sm:text-center">
                                Your host still needs to pin the Home Coming catalog program on Sacred Home — then checkout opens
                                here with your dates.
                              </p>
                            ) : null}
                          </>
                        )}
                      </>
                  </div>
                  ) : null}
                  {useCollapsiblePriorSacredCycle && priorPeriodMeta ? (
                    <div
                      className="rounded-2xl border border-white/80 bg-white/58 px-3 py-2 shadow-sm shadow-violet-200/40 text-left"
                      data-testid="home-coming-prior-cycle-collapsible"
                    >
                      <button
                        type="button"
                        className="flex w-full items-center justify-between gap-2 text-left min-h-[2.5rem]"
                        onClick={() => setPriorSacredCycleOpen((o) => !o)}
                        aria-expanded={priorSacredCycleOpen}
                      >
                        <span className="text-[10px] uppercase tracking-[0.14em] text-[rgba(100,55,155,0.5)] font-semibold">
                          Previous Sacred Home cycle — tap to show details
                        </span>
                        <ChevronDown
                          className={cn(
                            'h-4 w-4 shrink-0 text-[#7c3aed] transition-transform duration-200',
                            priorSacredCycleOpen ? 'rotate-180' : '',
                          )}
                          aria-hidden
                        />
                      </button>
                      {priorSacredCycleOpen ? (
                        <div className="mt-2 border-t border-violet-100/80 pt-2 text-[10px] sm:text-[11px] leading-relaxed text-[rgba(60,35,115,0.88)]">
                          <p className="flex flex-wrap items-baseline gap-x-1.5 gap-y-1 font-medium tabular-nums">
                            <span className="font-bold text-[#3b0764] uppercase tracking-wide">
                              {priorCycleYearLabel}
                            </span>
                            <span className="text-[rgba(100,55,155,0.45)]">·</span>
                            <span>
                              {priorPeriodMeta.start_date
                                ? formatDateDdMonYyyy(priorPeriodMeta.start_date)
                                : '—'}{' '}
                              –{' '}
                              {priorPeriodMeta.end_date
                                ? formatDateDdMonYyyy(priorPeriodMeta.end_date)
                                : '—'}
                            </span>
                            <span className="text-[rgba(100,55,155,0.45)]">·</span>
                            {Object.keys(HOME_COMING_PILLAR_SHORT).map((key, i) => {
                              const d = priorPillarFirstDates[key];
                              const label = HOME_COMING_PILLAR_SHORT[key];
                              return (
                                <React.Fragment key={key}>
                                  {i > 0 ? (
                                    <span className="text-[rgba(100,55,155,0.35)]">·</span>
                                  ) : null}
                                  <span>
                                    <span className="text-[rgba(100,55,155,0.5)] uppercase text-[9px] font-semibold">
                                      {label}
                                    </span>{' '}
                                    {d ? (
                                      <span className="font-semibold text-[#3b0764]">{formatDateDdMonYyyy(d)}</span>
                                    ) : (
                                      <span className="text-[rgba(100,55,155,0.4)]">—</span>
                                    )}
                                  </span>
                                </React.Fragment>
                              );
                            })}
                          </p>
                        </div>
                      ) : null}
                    </div>
                  ) : null}
                </div>
              </div>
            </div>
            </div>
            <p className="mt-4 max-w-[min(40rem,100%)] md:max-w-[min(50rem,100%)] mx-auto text-[13px] sm:text-sm italic leading-relaxed font-[family-name:'Playfair_Display',Georgia,serif] text-[rgba(90,40,135,0.55)]">
              {HEART_QUOTE}
              <Heart className="inline-block ml-1.5 w-3 h-3 text-rose-400/70 align-middle" aria-hidden />
            </p>
          </div>

          <div className="flex flex-col gap-5 px-5 md:px-8 pb-7 md:pb-9 pt-2">
            {catalogBundle ? (
              <div
                className={cn(glassInset, 'relative text-center sm:text-left')}
                data-testid="home-coming-catalog-total"
              >
                <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between sm:gap-4 mb-2 pr-0 sm:pr-1">
                  <div className="min-w-0 flex items-start gap-2 text-left">
                    <ShoppingCart size={18} className="text-[#5D3FD3] shrink-0 mt-1" aria-hidden />
                    <div className="min-w-0">
                      <h2
                        className="text-base sm:text-lg md:text-xl font-[family-name:'Cinzel',serif] font-bold text-[#3b0764] tracking-[0.12em] uppercase leading-tight"
                        data-testid="home-coming-annual-program-title"
                      >
                        Home Coming Annual Program
                      </h2>
                      <p className="text-[11px] text-[rgba(60,35,115,0.55)] mt-1.5 leading-snug tracking-normal font-normal">
                        {preferOnFilePackagePricing
                          ? `${catalogBundle.package_name || 'Home Coming'} · ${typeof catalogBundle.duration_months === 'number' ? `${catalogBundle.duration_months}-month bundle` : 'Annual bundle'} — your active cycle is on file; catalog totals are for future renewals.`
                          : (
                            <>
                              {catalogBundle.package_name || 'Home Coming'}
                              {typeof catalogBundle.duration_months === 'number'
                                ? ` · ${catalogBundle.duration_months}-month bundle`
                                : ''}
                              {' — '}totals from your Sacred Home catalog offer.
                            </>
                          )}
                      </p>
                    </div>
                  </div>
                  {!suppressHomeComingCartAndBrowse ? (
                  <Button
                    type="button"
                    variant="outline"
                    className="h-9 shrink-0 gap-2 border-[rgba(160,80,220,0.28)] bg-white/90 hover:bg-violet-50/90 text-slate-800 shadow-sm sm:mt-1 self-end sm:self-start"
                    onClick={() => navigate('/dashboard/combined-checkout')}
                  >
                    <CreditCard size={15} className="text-violet-700 shrink-0" />
                    <span className="text-[10px] font-bold uppercase tracking-wide">Divine cart</span>
                    {cartCount > 0 ? (
                      <span className="min-w-[1.15rem] h-5 px-1 rounded-full bg-violet-600 text-white text-[9px] font-bold tabular-nums flex items-center justify-center">
                        {cartCount}
                      </span>
                    ) : null}
                  </Button>
                  ) : null}
                </div>
                <div className="mt-4 grid grid-cols-1 lg:grid-cols-2 lg:gap-0 lg:divide-x lg:divide-[rgba(160,100,240,0.14)]">
                  <div className="min-w-0 space-y-3 lg:pr-8 lg:py-0.5">
                    {showRenewalSacredExchangePrice ? (
                      <div className="space-y-2 text-left">
                        <p className="text-[11px] uppercase tracking-[0.14em] font-semibold text-[rgba(100,55,155,0.45)]">
                          Your renewal energy exchange
                        </p>
                        {onFilePackageCourtesyBreakdown != null &&
                        Number(onFilePackageCourtesyBreakdown.baseRaw || 0) > 0 ? (
                          <>
                            <p className="text-left">
                              <span className="text-xl sm:text-2xl font-semibold text-[rgba(80,55,145,0.45)] tabular-nums line-through decoration-[rgba(120,80,160,0.35)]">
                                {symbol}
                                {Number(
                                  toDisplay(Number(onFilePackageCourtesyBreakdown.baseRaw || 0)),
                                ).toLocaleString()}
                              </span>{' '}
                              <span className="text-sm font-medium text-[rgba(80,55,145,0.45)]">
                                {(sacredMoneyCurrency || 'INR')}
                              </span>
                            </p>
                            <div className="rounded-xl border border-[rgba(212,175,55,0.35)] bg-gradient-to-r from-[#fffbf0]/95 via-white/90 to-[#fdf6e3]/90 px-3.5 py-3 text-left shadow-sm shadow-amber-900/5">
                              <p className="text-[12px] text-[#5c4a12] leading-snug">
                                Courtesy savings:{' '}
                                <strong className="text-[#6b5210] tabular-nums">
                                  {symbol}
                                  {Number(
                                    toDisplay(Number(onFilePackageCourtesyBreakdown.discountAmt || 0)),
                                  ).toLocaleString()}
                                </strong>
                                {onFilePackageCourtesyBreakdown.pctLabel ? (
                                  <span className="text-[#6b5210]">
                                    {' '}
                                    ({onFilePackageCourtesyBreakdown.pctLabel})
                                  </span>
                                ) : null}
                              </p>
                            </div>
                          </>
                        ) : null}
                        <p className="text-left">
                          <span className="text-[2rem] sm:text-[2.35rem] font-bold text-[#1a0a3d] tabular-nums tracking-tight">
                            {symbol}
                            {Number(
                              toDisplay(
                                Number(
                                  onFileCourtesyAdjustedTotal != null
                                    ? onFileCourtesyAdjustedTotal
                                    : fin.total_fee || 0,
                                ),
                              ),
                            ).toLocaleString()}
                          </span>{' '}
                          <span className="text-lg font-semibold text-[rgba(80,55,145,0.55)]">
                            {(sacredMoneyCurrency || 'AED')}
                          </span>
                        </p>
                        <p className="text-[11px] text-[rgba(60,35,115,0.65)] leading-snug">
                          This total matches your chosen payment schedule below and your Sacred Exchange record.
                        </p>
                        {catalogAmountHub != null && catalogDisplayAmount != null ? (
                          <p className="text-[10px] text-[rgba(90,55,135,0.5)] leading-snug pt-1 border-t border-violet-100/80 mt-2">
                            <span className="font-semibold uppercase tracking-wide text-[9px] text-[rgba(100,55,155,0.4)]">
                              Catalog reference (list pricing)
                            </span>{' '}
                            — {symbol}
                            {Number(
                              catalogCourtesyBreakdown
                                ? catalogCourtesyBreakdown.finalDisplay
                                : catalogDisplayAmount,
                            ).toLocaleString()}{' '}
                            {(baseCurrency || 'inr').toUpperCase()}
                          </p>
                        ) : null}
                      </div>
                    ) : preferOnFilePackagePricing ? (
                      <div className="space-y-2 text-left">
                        <p className="text-[11px] uppercase tracking-[0.14em] font-semibold text-[rgba(100,55,155,0.45)]">
                          Your package amount (on file)
                        </p>
                        <p className="text-left">
                          <span className="text-[2rem] sm:text-[2.35rem] font-bold text-[#1a0a3d] tabular-nums tracking-tight">
                            {symbol}
                            {Number(
                              toDisplay(
                                Number(
                                  onFileCourtesyAdjustedTotal != null
                                    ? onFileCourtesyAdjustedTotal
                                    : fin.total_fee || 0,
                                ),
                              ),
                            ).toLocaleString()}
                          </span>{' '}
                          <span className="text-lg font-semibold text-[rgba(80,55,145,0.55)]">
                            {(sacredMoneyCurrency || 'AED')}
                          </span>
                        </p>
                        <p className="text-[11px] text-[rgba(60,35,115,0.65)] leading-snug">
                          This is the annual energy exchange recorded for your current Sacred Home cycle
                          {lastAnnualCycleDisplayName ? (
                            <>
                              {' '}
                              (<span className="font-medium">{lastAnnualCycleDisplayName}</span>)
                            </>
                          ) : null}
                          . Inclusions for this path are listed on the right.
                        </p>
                        {catalogAmountHub != null && catalogDisplayAmount != null ? (
                          <p className="text-[10px] text-[rgba(90,55,135,0.5)] leading-snug pt-1 border-t border-violet-100/80 mt-2">
                            <span className="font-semibold uppercase tracking-wide text-[9px] text-[rgba(100,55,155,0.4)]">
                              Catalog reference (new renewals)
                            </span>{' '}
                            — list offer {symbol}
                            {Number(
                              catalogCourtesyBreakdown
                                ? catalogCourtesyBreakdown.finalDisplay
                                : catalogDisplayAmount,
                            ).toLocaleString()}{' '}
                            {(baseCurrency || 'inr').toUpperCase()} (not a new balance for your active cycle).
                          </p>
                        ) : null}
                      </div>
                    ) : catalogAmountHub != null && catalogDisplayAmount != null ? (
                      <>
                        {catalogCourtesyBreakdown ? (
                          <>
                            <p className="text-[11px] text-left text-[rgba(60,35,115,0.5)]">
                              <span className="uppercase tracking-[0.12em] font-semibold text-[rgba(100,55,155,0.45)]">
                                Catalog price
                              </span>
                            </p>
                            <p className="text-left">
                              <span className="text-xl sm:text-2xl font-semibold text-[rgba(80,55,145,0.45)] tabular-nums line-through decoration-[rgba(120,80,160,0.35)]">
                                {symbol}
                                {Number(catalogCourtesyBreakdown.listDisplay).toLocaleString()}
                              </span>{' '}
                              <span className="text-sm font-medium text-[rgba(80,55,145,0.45)]">
                                {(baseCurrency || 'inr').toUpperCase()}
                              </span>
                            </p>
                            <div className="rounded-xl border border-[rgba(212,175,55,0.35)] bg-gradient-to-r from-[#fffbf0]/95 via-white/90 to-[#fdf6e3]/90 px-3.5 py-3 text-left shadow-sm shadow-amber-900/5">
                              <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#8b6914] mb-1">
                                {catalogCourtesyBreakdown.ruleLabel}
                              </p>
                              <p className="text-[12px] text-[#5c4a12] leading-snug">
                                {catalogCourtesyBreakdown.pctLabel ? (
                                  <>
                                    <strong className="text-[#6b5210]">{catalogCourtesyBreakdown.pctLabel}</strong> off your
                                    catalog bundle
                                  </>
                                ) : catalogCourtesyBreakdown.isAmountBand ? (
                                  <>
                                    <strong className="text-[#6b5210] tabular-nums">
                                      {symbol}
                                      {Number(catalogCourtesyBreakdown.discountAmt).toLocaleString()}
                                    </strong>{' '}
                                    courtesy on this bundle
                                  </>
                                ) : (
                                  <>
                                    Courtesy savings:{' '}
                                    <strong className="text-[#6b5210] tabular-nums">
                                      {symbol}
                                      {Number(catalogCourtesyBreakdown.discountAmt).toLocaleString()}
                                    </strong>
                                  </>
                                )}
                              </p>
                            </div>
                            <div className="pt-1">
                              <p className="text-[11px] uppercase tracking-[0.14em] font-semibold text-[#3b0764]/80 mb-1 text-left">
                                Final price
                              </p>
                              <p className="text-left">
                                <span className="text-[2rem] sm:text-[2.35rem] font-bold text-[#1a0a3d] tabular-nums tracking-tight">
                                  {symbol}
                                  {Number(catalogCourtesyBreakdown.finalDisplay).toLocaleString()}
                                </span>{' '}
                                <span className="text-lg font-semibold text-[rgba(80,55,145,0.55)]">
                                  {(baseCurrency || 'inr').toUpperCase()}
                                </span>
                              </p>
                            </div>
                          </>
                        ) : (
                          <p className="mt-0 text-left">
                            <span className="text-[2rem] sm:text-[2.35rem] font-bold text-[#1a0a3d] tabular-nums tracking-tight">
                              {symbol}
                              {Number(catalogDisplayAmount).toLocaleString()}
                            </span>{' '}
                            <span className="text-lg font-semibold text-[rgba(80,55,145,0.55)]">
                              {(baseCurrency || 'inr').toUpperCase()}
                            </span>
                          </p>
                        )}
                      </>
                    ) : crmSubscriptionFeeMatchesHub && Number(fin.total_fee || 0) > 0 ? (
                      <div className="space-y-2 text-left">
                        <p className="text-[11px] uppercase tracking-[0.14em] font-semibold text-[rgba(100,55,155,0.45)]">
                          Your annual exchange
                        </p>
                        <p className="text-left">
                          <span className="text-[2rem] sm:text-[2.35rem] font-bold text-[#1a0a3d] tabular-nums tracking-tight">
                            {symbol}
                            {Number(toDisplay(Number(fin.total_fee || 0))).toLocaleString()}
                          </span>{' '}
                          <span className="text-lg font-semibold text-[rgba(80,55,145,0.55)]">
                            {sacredMoneyCurrency || 'AED'}
                          </span>
                        </p>
                        <p className="text-[10px] text-[rgba(60,35,115,0.55)] leading-snug">
                          From <span className="font-semibold">Iris Annual Abundance</span> in your portal hub (
                          {(baseCurrency || 'inr').toUpperCase()}). For a public list price in this hub, your host can add{' '}
                          {(baseCurrency || 'aed').toUpperCase()} under <span className="font-semibold">Home Coming catalog</span>{' '}
                          → package offer.
                        </p>
                        {catalogAllPositiveEntriesCourtesyAdjusted.length > 0 ? (
                          <div className="pt-2 mt-2 border-t border-violet-100/80 space-y-1">
                            <p className="text-[9px] uppercase tracking-wide text-[rgba(100,55,155,0.4)] font-semibold">
                              Catalog (other hubs)
                            </p>
                            {catalogAllPositiveEntriesCourtesyAdjusted.map(([k, v]) => (
                              <p
                                key={String(k)}
                                className="text-[11px] font-semibold tabular-nums text-[rgba(60,35,115,0.75)]"
                              >
                                {String(k).toUpperCase()} {Number(v).toLocaleString()}
                              </p>
                            ))}
                          </div>
                        ) : null}
                      </div>
                    ) : catalogAllPositiveEntriesCourtesyAdjusted.length > 0 ? (
                      <div className="space-y-2 text-left">
                        {primaryCatalogFallbackCourtesyBreakdown?.changed ? (
                          <>
                            <p className="text-[11px] uppercase tracking-[0.14em] font-semibold text-[rgba(100,55,155,0.45)]">
                              Catalog price ({primaryCatalogFallbackCourtesyBreakdown.currencyKey})
                            </p>
                            <p className="text-left">
                              <span className="text-xl sm:text-2xl font-semibold text-[rgba(80,55,145,0.45)] tabular-nums line-through decoration-[rgba(120,80,160,0.35)]">
                                {symbol}
                                {Number(primaryCatalogFallbackCourtesyBreakdown.listNum).toLocaleString()}
                              </span>
                            </p>
                            <div className="rounded-xl border border-[rgba(212,175,55,0.35)] bg-gradient-to-r from-[#fffbf0]/95 via-white/90 to-[#fdf6e3]/90 px-3.5 py-3 text-left shadow-sm shadow-amber-900/5">
                              <p className="text-[12px] text-[#5c4a12] leading-snug">
                                Courtesy savings:{' '}
                                <strong className="text-[#6b5210] tabular-nums">
                                  {symbol}
                                  {Number(primaryCatalogFallbackCourtesyBreakdown.discountAmt).toLocaleString()}
                                </strong>
                                {primaryCatalogFallbackCourtesyBreakdown.pctLabel ? (
                                  <span className="text-[#6b5210]">
                                    {' '}
                                    ({primaryCatalogFallbackCourtesyBreakdown.pctLabel})
                                  </span>
                                ) : null}
                              </p>
                            </div>
                            <p className="text-left">
                              <span className="text-[2rem] sm:text-[2.35rem] font-bold text-[#1a0a3d] tabular-nums tracking-tight">
                                {symbol}
                                {Number(primaryCatalogFallbackCourtesyBreakdown.finalNum).toLocaleString()}
                              </span>{' '}
                              <span className="text-lg font-semibold text-[rgba(80,55,145,0.55)]">
                                {primaryCatalogFallbackCourtesyBreakdown.currencyKey}
                              </span>
                            </p>
                          </>
                        ) : (
                          <div className="space-y-1">
                            {catalogAllPositiveEntriesCourtesyAdjusted.map(([k, v]) => (
                              <p
                                key={String(k)}
                                className="text-[1.35rem] sm:text-[1.5rem] font-bold text-[#1a0a3d] tabular-nums"
                              >
                                <span className="text-base font-semibold text-[rgba(80,55,145,0.55)] mr-1.5 inline-block">
                                  {String(k).toUpperCase()}
                                </span>
                                {Number(v).toLocaleString()}
                              </p>
                            ))}
                          </div>
                        )}
                        {catalogAmountHub == null ? (
                          <p className="text-[10px] text-[rgba(60,35,115,0.45)] mt-2 max-w-xl leading-relaxed">
                            No catalog amount published for your current pricing hub (
                            {(baseCurrency || 'inr').toUpperCase()}). Listed amounts above are entered in Admin.
                          </p>
                        ) : null}
                      </div>
                    ) : (
                      <p className="text-sm text-amber-900/90 bg-amber-50/95 border border-amber-200/80 rounded-xl px-3 py-2.5 leading-snug text-left">
                        Your host has not entered the catalog package offer total yet. They set it under{' '}
                        <strong>Admin → Home Coming catalog → Package offer</strong>
                        {' — '}then your total annual price will show here.
                      </p>
                    )}
                    {catalogOtherCurrencies.length > 0 && catalogAmountHub != null ? (
                      <p className="text-[10px] text-[rgba(60,35,115,0.48)] leading-relaxed text-left">
                        Other pricing hubs:{' '}
                        {catalogOtherCurrencies.map(([k, v], idx) => (
                          <span key={`${k}-${idx}`}>
                            {idx > 0 ? ' · ' : ''}
                            <span className="font-semibold tabular-nums">
                              {String(k).toUpperCase()} {Number(v).toLocaleString()}
                            </span>
                          </span>
                        ))}
                      </p>
                    ) : null}
                  </div>
                  <div className="min-w-0 lg:pl-8 lg:pr-8 lg:py-0.5 pt-6 lg:pt-0 border-t border-slate-200 lg:border-t-0 text-left">
                    <div className="rounded-2xl border border-slate-200 bg-white px-4 py-4 sm:px-5 sm:py-5 shadow-sm">
                    <p className="font-[family-name:'Playfair_Display',Georgia,serif] text-[12px] sm:text-[13px] leading-relaxed text-slate-700">
                      One gentle annual home for your soul — what is held in this bundle:
                    </p>
                    <ul className="mt-3 space-y-2.5 text-[11px] sm:text-[12px] font-extrabold uppercase tracking-[0.09em] text-slate-800 list-none pl-0 border-l-2 border-amber-200/80 pl-3">
                      {HOME_COMING_BUNDLE_INCLUDES_LINES.map((line) => (
                        <li key={line} className="leading-snug">
                          {line}
                        </li>
                      ))}
                    </ul>
                    {(catalogFrom || catalogTo) && (
                      <p className="text-[11px] mt-4 text-[#6b4420] bg-[rgba(255,251,235,0.85)] border border-[rgba(212,175,55,0.28)] rounded-xl px-3 py-2.5 text-left">
                        Offer window{catalogFrom ? ` from ${formatDateDdMonYyyy(catalogFrom)}` : ''}
                        {catalogTo ? ` · to ${formatDateDdMonYyyy(catalogTo)}` : ''} — when this catalog bundle may be purchased.
                      </p>
                    )}
                    </div>
                  </div>
                </div>
              </div>
            ) : null}

            {!pinnedProgram ? (
              <div className={cn(glassInset, 'text-center py-10')}>
                <User className="mx-auto text-violet-300/90 mb-4" size={42} aria-hidden />
                <p className="font-[family-name:'Cinzel',serif] text-[#3b0764] font-semibold text-base tracking-wide">
                  The path is forming…
                </p>
                <p className="text-sm text-[rgba(60,35,115,0.55)] mt-2 max-w-md mx-auto leading-relaxed">
                  Your host can pin the Home Coming catalog program under Sacred Home settings, or go to{' '}
                  <Link to="/dashboard#sacred-home-programs" className="font-semibold text-[#7c3aed] hover:underline">
                    Upcoming programs
                  </Link>{' '}
                  from your overview.
                </p>
              </div>
            ) : (
              <div className="space-y-5">
                {!catalogBundle ? (
                  <div className="rounded-2xl border border-slate-200 bg-white px-4 py-4 md:px-5 md:py-5 shadow-sm text-left">
                    <p className="font-[family-name:'Playfair_Display',Georgia,serif] text-[12px] sm:text-[13px] leading-relaxed text-slate-700">
                      One gentle annual home for your soul — what is held in this bundle:
                    </p>
                    <ul className="mt-3 space-y-2.5 text-[11px] sm:text-[12px] font-extrabold uppercase tracking-[0.09em] text-slate-800 list-none pl-0 border-l-2 border-amber-200/80 pl-3">
                      {HOME_COMING_BUNDLE_INCLUDES_LINES.map((line) => (
                        <li key={line}>{line}</li>
                      ))}
                    </ul>
                  </div>
                ) : null}

                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
                  <div className="space-y-2 min-w-0">
                    <Label className="text-[11px] uppercase tracking-[0.12em] text-[rgba(70,35,125,0.65)] font-semibold">
                      Payment structure (preference)
                    </Label>
                    <Select value={paymentMode} onValueChange={setPaymentMode}>
                      <SelectTrigger
                        className="h-10 w-full max-w-none md:max-w-none border-[rgba(160,80,220,0.22)] bg-white/75 text-[13px] font-medium text-[rgba(50,35,95,0.92)] uppercase"
                        data-testid="annual-offer-payment-mode-select"
                      >
                        <SelectValue placeholder="CHOOSE HOW YOU WISH TO PAY" />
                      </SelectTrigger>
                      <SelectContent>
                        {visiblePayModes.map((m) => (
                          <SelectItem
                            key={m.value}
                            value={m.value}
                            className="text-[13px] uppercase"
                            data-testid={`annual-offer-mode-${m.value}`}
                          >
                            {m.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p
                      className="text-[10px] sm:text-[11px] font-bold uppercase tracking-[0.07em] text-[rgba(90,45,140,0.9)] leading-snug pt-0.5"
                      data-testid="annual-offer-other-schedule-contact-healer"
                    >
                      TO CHOOSE OTHER PAYMENT SCHEDULE, CONTACT YOUR HEALER.
                    </p>
                  </div>
                  <div className="space-y-2 min-w-0">
                    <Label className="text-[11px] uppercase tracking-[0.12em] text-[rgba(70,35,125,0.65)] font-semibold">
                      Participation (annual path)
                    </Label>
                    <Select value={annualParticipationMode} onValueChange={setAnnualParticipationMode}>
                      <SelectTrigger
                        className="h-10 w-full max-w-none md:max-w-none border-[rgba(160,80,220,0.22)] bg-white/75 text-[13px] font-medium text-[rgba(50,35,95,0.92)] uppercase"
                        data-testid="annual-offer-participation-select"
                      >
                        <SelectValue placeholder="ONLINE OR OFFLINE" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="online" className="text-[13px] uppercase" data-testid="annual-offer-participation-online">
                          ONLINE
                        </SelectItem>
                        <SelectItem value="offline" className="text-[13px] uppercase" data-testid="annual-offer-participation-offline">
                          OFFLINE
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  </div>
                  <p className="text-[10px] text-[rgba(60,35,115,0.48)] leading-relaxed">
                    Home Coming annual programs use online or offline participation only — there is no in-person seat type for this bundle.
                  </p>
                  <p className="text-[10px] text-[rgba(60,35,115,0.5)] flex items-start gap-1.5 leading-relaxed">
                    <Info size={12} className="shrink-0 mt-0.5 text-violet-500" aria-hidden />
                    We save your payment choice for your host. Your healer can turn off individual EMI or Flexi options in Dashboard access if a simpler path is needed.
                  </p>
                </div>

                {needsAdminEmiBackfillHint ? (
                  <div
                    className="rounded-xl border border-sky-200/90 bg-sky-50/95 px-3 py-2.5 text-left text-[11px] text-sky-950 leading-relaxed"
                    data-testid="annual-offer-admin-emi-hint"
                  >
                    <span className="font-semibold">Paid before Sacred Exchange tracked it?</span> Your host can record that
                    in <span className="font-semibold">Admin → Iris Annual Abundance</span> (open your row → save fee / EMIs)
                    or under <span className="font-semibold">Subscribers</span>: set installment status to{' '}
                    <span className="font-semibold">paid</span>, add paid date / receipt if needed — refresh here and the
                    schedule below will mirror your garden record.
                  </div>
                ) : null}

                {pinnedProgram && paymentScheduleRows.length > 0 ? (
                  <div
                    className="rounded-2xl border border-[rgba(160,100,240,0.15)] bg-white/40 backdrop-blur-sm overflow-hidden"
                    data-testid="annual-offer-payment-schedule"
                  >
                    <p className="text-[10px] font-bold uppercase tracking-[0.12em] text-[rgba(80,55,145,0.55)] px-3 py-2.5 bg-[rgba(250,245,255,0.7)] border-b border-[rgba(160,100,240,0.08)]">
                      {scheduleTitle}
                    </p>
                    <div className="overflow-x-auto">
                      <table className="w-full min-w-[720px] table-fixed border-collapse text-center text-[10px]">
                        <colgroup>
                          <col style={{ width: '7%' }} />
                          <col style={{ width: '14%' }} />
                          <col style={{ width: '16%' }} />
                          <col style={{ width: '14%' }} />
                          <col style={{ width: '14%' }} />
                          <col style={{ width: '14%' }} />
                          <col style={{ width: '21%' }} />
                        </colgroup>
                        <thead>
                          <tr className="bg-[rgba(252,250,255,0.95)] border-b border-[rgba(160,100,240,0.12)] text-[rgba(80,55,145,0.75)]">
                            <th className="font-bold uppercase tracking-wide px-1.5 py-2 align-bottom whitespace-nowrap">
                              Sr #
                            </th>
                            <th className="font-bold uppercase tracking-wide py-2 pl-1 pr-5 align-bottom whitespace-nowrap -translate-x-0.5">
                              Energy Exchange
                            </th>
                            <th className="font-bold uppercase tracking-wide py-2 pl-3 pr-1.5 align-bottom whitespace-nowrap">
                              Due date
                            </th>
                            <th className="font-bold uppercase tracking-wide px-1.5 py-2 align-bottom whitespace-nowrap">
                              Total amount
                            </th>
                            <th className="font-bold uppercase tracking-wide px-1.5 py-2 align-bottom whitespace-nowrap">
                              Pay here
                            </th>
                            <th className="font-bold uppercase tracking-wide px-1.5 py-2 align-bottom whitespace-nowrap">
                              Paid date
                            </th>
                            <th className="font-bold uppercase tracking-wide px-1.5 py-2 align-bottom whitespace-nowrap">
                              Receipt
                            </th>
                          </tr>
                        </thead>
                        <tbody className="text-[rgba(50,35,95,0.9)]">
                          {paymentScheduleRows.map((row) => {
                            const dueStr = row.dueDisplay ?? (row.due ? formatDateDdMonYyyy(row.due) : '—');
                            const hasNumericAmount =
                              row.amount != null && row.amountDisplay == null && !Number.isNaN(Number(row.amount));
                            const energyExchangeShown =
                              row.amountDisplay != null
                                ? row.amountDisplay
                                : hasNumericAmount
                                  ? `${symbol}${Number(toDisplay(row.amount)).toLocaleString()}`
                                  : '—';
                            const totalAmountShown =
                              hasNumericAmount
                                ? `${symbol}${Number(toDisplay(Number(row.amount))).toLocaleString()}`
                                : energyExchangeShown;
                            const isFlexiChooseRow =
                              paymentMode === 'emi_flexi' && String(row.key || '') === 'flex-a';
                            const emiRow =
                              typeof row.n === 'number' && !Number.isNaN(row.n)
                                ? emiByNumber.get(row.n)
                                : null;
                            const portalPayFullMatchesRow =
                              portalHomeComingPaidInfo &&
                              paymentMode === 'full' &&
                              String(row.key || '') === 'pay-full' &&
                              hasNumericAmount &&
                              Math.abs(
                                Number(portalHomeComingPaidInfo.amount) - Number(row.amount),
                              ) <= Math.max(2, Math.abs(Number(row.amount)) * 0.025);
                            const crmEmiPaid = emiRow && String(emiRow.status || '').toLowerCase() === 'paid';
                            const portalEmiMatch = portalEmiInstallmentMatches.get(row.key) || null;
                            const syntheticPaidRow = Boolean(
                              (portalPayFullMatchesRow && !crmEmiPaid) || (portalEmiMatch && !crmEmiPaid),
                            );
                            const emiRowEffective =
                              crmEmiPaid
                                ? emiRow
                                : syntheticPaidRow
                                  ? {
                                      status: 'paid',
                                      paid_date:
                                        portalEmiMatch?.paidAtYmd ||
                                        portalHomeComingPaidInfo?.paidAtYmd ||
                                        '',
                                      payment_method:
                                        portalEmiMatch?.paymentMethod ||
                                        'stripe',
                                    }
                                  : emiRow;
                            let paidDateCell = '—';
                            if (emiRowEffective && emiRowEffective.status === 'paid') {
                              const rawPd = emiRowEffective.paid_date || emiRowEffective.date || '';
                              paidDateCell = rawPd
                                ? formatDateDdMonYyyy(String(rawPd).slice(0, 10)) || '—'
                                : 'Paid';
                            }
                            const payHereLabel = emiRowEffective?.payment_method
                              ? formatSchedulePayTag(emiRowEffective.payment_method)
                              : schedulePayTag;
                            const rawRc = emiRow?.receipt_url && String(emiRow.receipt_url).trim();
                            let receiptFromEmi = null;
                            if (rawRc) {
                              if (/^https?:\/\//i.test(rawRc)) {
                                receiptFromEmi = rawRc;
                              } else if (typeof window !== 'undefined' && rawRc.startsWith('/')) {
                                receiptFromEmi = `${window.location.origin}${rawRc}`;
                              } else {
                                const base = getApiUrl().replace(/\/$/, '');
                                receiptFromEmi = `${base}/${String(rawRc).replace(/^\//, '')}`;
                              }
                            }
                            const syntheticSessionId =
                              portalEmiMatch?.stripeSessionId || portalHomeComingPaidInfo?.stripeSessionId || '';
                            const receiptHref =
                              receiptFromEmi ||
                              (syntheticPaidRow && syntheticSessionId
                                ? `${window.location.origin}/payment/success?session_id=${encodeURIComponent(syntheticSessionId)}`
                                : null);
                            const payHereOpensEmiModal =
                              typeof row.n === 'number' &&
                              !Number.isNaN(row.n) &&
                              emiRow &&
                              emiRow.status !== 'paid' &&
                              emiRow.status !== 'submitted';
                            const onPayHereClick = () => {
                              if (payHereOpensEmiModal) {
                                navigate(`/dashboard/financials?payEmi=${row.n}`);
                                return;
                              }
                              goCheckout(row);
                            };
                            return (
                              <tr
                                key={row.key}
                                className="border-b border-[rgba(160,100,240,0.08)] last:border-b-0 align-top"
                              >
                                <td className="px-1.5 py-2.5 align-middle tabular-nums">
                                  <span className="font-bold text-[#3b0764]">{row.n}</span>
                                </td>
                                <td className="py-2.5 pl-1 pr-5 align-middle font-semibold tabular-nums break-words -translate-x-0.5">
                                  {energyExchangeShown}
                                </td>
                                <td className="py-2.5 pl-3 pr-1.5 align-middle break-words text-[9px] sm:text-[10px] font-medium">
                                  {dueStr}
                                </td>
                                <td className="px-1.5 py-2.5 align-middle font-semibold tabular-nums break-words">
                                  {isFlexiChooseRow ? (
                                    <div className="mx-auto flex max-w-[9.5rem] flex-col items-stretch gap-1">
                                      <span className="text-[8px] font-semibold uppercase tracking-wide text-[rgba(80,55,145,0.55)] text-center leading-tight">
                                        Your amount
                                      </span>
                                      <div className="flex items-center justify-center gap-0.5">
                                        <span className="text-[10px] shrink-0 tabular-nums">{symbol}</span>
                                        <Input
                                          type="text"
                                          inputMode="decimal"
                                          autoComplete="off"
                                          placeholder="0"
                                          value={flexiAmountInput}
                                          onChange={(e) => setFlexiAmountInput(e.target.value)}
                                          className="h-8 min-w-0 w-[5.25rem] text-center text-[11px] font-medium tabular-nums px-1.5 py-0"
                                          data-testid="annual-offer-flexi-amount"
                                        />
                                      </div>
                                    </div>
                                  ) : (
                                    totalAmountShown
                                  )}
                                </td>
                                <td className="px-1.5 py-2.5 align-middle">
                                  {emiRowEffective?.status === 'paid' ? (
                                    <span
                                      className="mx-auto inline-flex max-w-full min-w-0 items-center justify-center rounded-md bg-emerald-100/90 px-1.5 py-1 text-[8px] font-semibold uppercase leading-tight tracking-wide text-emerald-900"
                                      title="This installment is paid"
                                    >
                                      Paid · {payHereLabel}
                                    </span>
                                  ) : emiRowEffective?.status === 'submitted' ? (
                                    <button
                                      type="button"
                                      onClick={() => navigate('/dashboard/financials')}
                                      className="mx-auto w-full max-w-full rounded-md border border-amber-200/90 bg-amber-50/90 px-1.5 py-1 text-[8px] font-semibold uppercase leading-tight tracking-wide text-amber-950 hover:bg-amber-100/90"
                                      title="Open Sacred Exchange to see proof status"
                                    >
                                      Pending review
                                    </button>
                                  ) : (
                                    <button
                                      type="button"
                                      onClick={onPayHereClick}
                                      className="mx-auto w-full max-w-full cursor-pointer rounded-md border border-violet-200/80 bg-violet-100/90 px-1.5 py-1 text-[8px] font-semibold uppercase leading-tight tracking-wide text-[#4c1d95] shadow-sm hover:bg-violet-200/70"
                                      title={
                                        payHereOpensEmiModal
                                          ? `Pay with ${payHereLabel} — opens checkout on Sacred Exchange`
                                          : 'Continue to checkout for your Home Coming bundle'
                                      }
                                    >
                                      Pay · {payHereLabel}
                                    </button>
                                  )}
                                </td>
                                <td className="px-1.5 py-2.5 align-middle break-words text-[9px] text-[rgba(80,55,145,0.75)] tabular-nums">
                                  {paidDateCell}
                                </td>
                                <td className="px-1.5 py-2.5 align-middle break-words">
                                  {receiptHref ? (
                                    <a
                                      href={receiptHref}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="mx-auto inline-flex max-w-full min-w-0 items-center justify-center gap-1 break-words text-[9px] font-semibold text-[#6d28d9] hover:text-[#5b21b6] hover:underline"
                                    >
                                      <Paperclip size={12} className="shrink-0 opacity-80" aria-hidden />
                                      Receipt file
                                    </a>
                                  ) : emiRowEffective?.status === 'paid' ? (
                                    <span className="text-[9px] text-[rgba(90,55,135,0.55)]">
                                      Receipt with host
                                    </span>
                                  ) : (
                                    <span className="text-[9px] text-[rgba(90,55,135,0.38)]">—</span>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                        {paymentScheduleNumericTotal > 0 ? (
                          <tfoot>
                            <tr className="bg-[rgba(244,240,255,0.95)] border-t-2 border-[rgba(160,100,240,0.22)] text-[#3b0764]">
                              <td colSpan={2} className="px-1.5 py-2.5 text-left text-[9px] font-bold uppercase tracking-wide">
                                TOTAL ROW
                                {paymentScheduleEmiRowCount > 0
                                  ? ` · ${paymentScheduleEmiRowCount} INSTALLMENT${paymentScheduleEmiRowCount !== 1 ? 'S' : ''}`
                                  : null}
                              </td>
                              <td className="py-2.5 text-[9px] text-[rgba(80,55,145,0.55)]">—</td>
                              <td className="px-1.5 py-2.5 text-[10px] font-bold tabular-nums">
                                {symbol}
                                {Number(toDisplay(paymentScheduleNumericTotal)).toLocaleString()}
                              </td>
                              <td colSpan={3} className="px-1.5 py-2.5 text-left">
                                <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-300/70 bg-emerald-50/95 px-2.5 py-1 text-[9px] font-bold uppercase tracking-wide text-emerald-800 shadow-sm">
                                  TOTAL PAID
                                  <span className="tabular-nums text-[10px] text-emerald-900">
                                    {symbol}
                                    {Number(toDisplay(paymentSchedulePaidTotal)).toLocaleString()}
                                  </span>
                                </span>
                              </td>
                            </tr>
                          </tfoot>
                        ) : null}
                      </table>
                    </div>
                    <p className="border-t border-[rgba(160,100,240,0.08)] bg-[rgba(250,245,255,0.4)] px-3 py-2 text-[9px] leading-snug text-[rgba(80,55,145,0.58)]">
                      First installment is due the <strong>month before</strong> your batch start on the <strong>27th</strong> (e.g. 27 Apr when the batch opens 3 May). Following rows stay on the 27th (or the last day in shorter months).
                    </p>
                  </div>
                ) : null}

                <div>
                  <Label className="text-[11px] uppercase tracking-[0.1em] text-[rgba(70,35,125,0.55)] font-semibold">Note for your host (optional)</Label>
                  <Input
                    value={emiNotes}
                    onChange={(e) => setEmiNotes(e.target.value.slice(0, 800))}
                    placeholder="e.g. align EMIs with salary date — anything your heart wishes your host to know"
                    className="h-10 mt-1.5 border-[rgba(160,80,220,0.22)] bg-white/70"
                    data-testid="annual-offer-emi-notes"
                  />
                </div>

                {!suppressHomeComingCartAndBrowse ? (
                <div className="flex flex-col sm:flex-row gap-3 pt-2">
                  <Button
                    type="button"
                    className="flex-1 h-11 bg-gradient-to-r from-violet-700 to-[#6d28d9] hover:from-violet-800 hover:to-violet-800 shadow-lg shadow-violet-900/20 text-[11px] font-bold uppercase tracking-wide"
                    disabled={!canContinueToCheckout}
                    onClick={goCheckout}
                    data-testid="annual-offer-checkout"
                  >
                    <ShoppingCart size={18} className="mr-2 shrink-0" />
                    CONTINUE TO DIVINE CART
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    className="flex-1 h-11 border-[rgba(160,80,220,0.35)] bg-white/60 hover:bg-white/90 text-[11px] font-bold uppercase tracking-wide"
                    asChild
                  >
                    <Link to="/dashboard#sacred-home-programs">BROWSE ALL UPCOMING PROGRAMS</Link>
                  </Button>
                </div>
                ) : null}
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
    <style>{`
      @keyframes fadeSlideUpHc {
        from { opacity: 0; transform: translateY(20px); }
        to { opacity: 1; transform: translateY(0); }
      }
    `}</style>
    </>
  );
}
